import OpenAI, { AzureOpenAI } from 'openai';
import {
  responseFormat as coachActionsResponseFormat,
  phaseSkeletonResponseFormat as coachActionsPhaseSkeletonFormat,
} from './utils/coachActionsSchema';

// ── R1: LLM-based intent classifier ──────────────────────────────────────────
// Replaces the regex in classifyIntent() as the primary routing signal.
// The regex method stays as a fallback (network blip, rate limit, etc.).
const INTENT_CLASSIFIER_PROMPT = `You are a strict intent classifier for a triathlon coaching app.
Classify the athlete's message as exactly one of five labels:

- plan_build: The athlete wants to CREATE, DESIGN, or RESTRUCTURE the overall
  TRAINING PLAN at a SEASON level — phases, periodization, the high-level
  structure for one or more races. This is about the plan skeleton, NOT
  individual workouts.
  Examples:
    - "build me a plan for my two races"
    - "help me design a season for Ironman"
    - "set up my training plan"
    - "rebuild / restructure my training plan"
    - "create a 16-week structure for Holkham"
    - "I want a periodized plan for the rest of the year"
    - "I have multiple races this season. How should I prioritise and organise them?"
    - "I'll be in Hong Kong for a month — how should we structure training around that?" (long trip, asking to restructure season)
  NOT plan_build (these are proposal_bearing):
    - "update my calendar" + describes a specific workout
    - "add a bike ride to my calendar"
    - "can you add tomorrow's session?"
      - "I'm going hiking June 3-8, it'll be a recovery week" → proposal_bearing (short, specific, bounded)
      - "I'll be away in the Peak District for a week, tapering" → proposal_bearing (short bounded event)

- populate_workouts: The athlete wants to populate, fill in, or schedule
  workouts for EXISTING phases or a defined period — the plan structure
  already exists and they want the sessions for it. Routes to a
  workouts-only tool profile (no phase changes allowed).
  Examples:
    - "populate the workouts for my base phase"
    - "fill in next week's sessions"
    - "give me workouts for the next 6 weeks"
    - "schedule workouts for the build phase"
    - "add sessions to the weeks I've set up"

- proposal_bearing: The athlete wants a SPECIFIC targeted change — a named,
  dated workout added or removed, a phase or milestone modified, or a
  targeted calendar update. Use this ONLY when the athlete is requesting
  that you MAKE the change, not just asking for advice about it.
  Examples:
    - "add a 30-minute easy run tomorrow"
    - "remove Wednesday's workout"
    - "shift my long ride to Sunday"
    - "I'm doing a Z2 bike ride in Regent's Park tomorrow, can you add it?"
    - "can you update my calendar?" + specific workout details
    - "add a Z3 bike, 90 minutes, this Saturday"
    - "add a milestone for my FTP test"
    - "swap Tuesday's run for a brick" (explicit request to make the swap)
    - "my legs are heavy — swap tomorrow's intervals for an easy spin"
    - "I'm going hiking in the Lake & Peak District June 3-8, so it will be a recovery and tapering week for me" (athlete informs about a short, bounded life-event window — implies targeted calendar + plan adjustments for those exact dates)
    - "I'll be travelling next week so only light activity" (short travel period with training implications)
    - "I'm going to be away from Monday to Friday, treat it as an off week" (short absence with training implications)

- coaching_question: A direct question seeking a short, conversational coaching
  answer (2–3 sentences). No plan or calendar change. This includes questions
  that mention a specific day or session but are asking for ADVICE about it,
  not requesting a change.
  Examples:
    - "what should I do today?"
    - "should I run or swim tomorrow?"
    - "why am I so tired this week?"
    - "is my training load too high?"
    - "how should I pace the bike leg?"
    - "is my CTL high enough?"
    - "my race is next week — what should I be aware of?"
    - "should I push through Saturday's long ride or take a rest day?" (advice, not a calendar change)
    - "I have a niggle in my Achilles — is it okay to swim today?" (health check, not a booking)
    - "I missed Tuesday's session — should I catch up or move on?" (advice, not asking to modify the calendar)
    - "my HRV is low — should I skip tomorrow's session?" (advice-seeking)

- coaching_analysis: The athlete wants a detailed explanation, breakdown, or
  analytical coaching insight based on their data. A longer structured response
  is appropriate.
  Examples:
    - "analyse my last 4 weeks of training"
    - "explain my CTL trend in detail"
    - "give me a full breakdown of my training imbalances"
    - "am I ready for my race? how ready am I?" (data-driven readiness assessment)
    - "can you review my swim data from this morning?" (session review / data analysis)
    - "my nutrition fell apart on today's long run — can we work on a fuelling strategy?"
    - "what are the best intervals for me right now?" (structured protocol recommendation)
    - "explain the 80/20 rule"
    - "why does TSB matter for race week?"

DISAMBIGUATION RULES:
1. A message mentions a specific session or date — use proposal_bearing ONLY if the
   athlete is asking you to ADD, REMOVE, or MODIFY it. If they are asking for ADVICE
   about that session ("should I do it?", "is it okay to skip?", "should I push through?"),
   use coaching_question instead.
2. Reserve plan_build for messages that are clearly about restructuring the season-level
   phase structure — multiple weeks, multiple races, or a named training period ("base",
   "build", "peak" block).
3. If the message is a short direct question with no analytical depth requested, use
   coaching_question.
4. If the message asks for a "review", "analysis", "breakdown", "assessment", or asks
   "how ready am I?", use coaching_analysis even if phrased conversationally.
5. "Should I swap X for Y?" — use proposal_bearing if X and Y are specific named
   sessions with a date; use coaching_question if it is asking whether swapping is a
   good idea without implying the athlete expects the change to be made right now.
6. Structural changes lasting multiple weeks or months ("organise my season",
   "structure training for a trip") are plan_build even if phrased as "how should I?".
7. When the athlete INFORMS you about a specific, SHORT period (days to ~2 weeks) when their
   training will be disrupted — travel, hiking, holiday, life event — use proposal_bearing.
   The implication is targeted calendar and/or phase changes for those exact dates, not a
   season restructure. Reserve plan_build for creating or restructuring the season skeleton
   across multiple phases or months.
   "I'll be hiking June 3-8, recovery week" = proposal_bearing.
   "How should we structure training around a month in Hong Kong?" = plan_build.

Reply with the JSON object only. No explanation, no preamble.`;

const INTENT_CLASSIFIER_SCHEMA = {
  type: 'json_schema',
  json_schema: {
    name: 'intent_classification',
    strict: true,
    schema: {
      type: 'object',
      properties: {
        intent: { type: 'string', enum: ['plan_build', 'proposal_bearing', 'populate_workouts', 'coaching_question', 'coaching_analysis'] },
      },
      required: ['intent'],
      additionalProperties: false,
    },
  },
};

// ── Output sanitizers for proactive AI messages ───────────────────────────────
// These run inside the generation methods before returning, so every caller
// automatically gets clean output regardless of how the result is used.

/**
 * Sanitizes bullet-format AI responses (• bullet1\n• bullet2\n• bullet3).
 * Extracts only lines beginning with •, discarding any trailing overflow or
 * incomplete sentence that the model appended after the last bullet.
 * Safe for metric values (e.g. "CTL 42.5") because it operates on whole lines,
 * not on sentence-boundary punctuation.
 */
function sanitizeBulletResponse(text) {
  if (!text || typeof text !== 'string') return text;
  const bullets = text
    .split('\n')
    .filter(l => l.trim().startsWith('•'))
    .map(l => l.trim());
  return bullets.length > 0 ? bullets.join('\n') : text.trim();
}

/**
 * Trims a prose AI response to the last complete sentence.
 * Scans for ". ", "! ", "? " boundaries and picks the last one where the
 * character immediately before the punctuation is NOT a digit (guards against
 * false positives like "CTL 42.5 is..." or "ACWR 1.3 means...").
 * Returns the original string unchanged if no safe boundary is found.
 */
function trimProseToLastSentence(text) {
  if (!text || typeof text !== 'string') return text;
  const trimmed = text.trim();
  if (/[.!?]['"]?$/.test(trimmed)) return trimmed; // already complete
  let bestIdx = -1;
  const endings = ['. ', '! ', '? ', '.\n', '!\n', '?\n'];
  for (const ending of endings) {
    let pos = 0;
    let idx;
    while ((idx = trimmed.indexOf(ending, pos)) !== -1) {
      const charBefore = idx > 0 ? trimmed[idx - 1] : '';
      if (!/\d/.test(charBefore)) bestIdx = Math.max(bestIdx, idx);
      pos = idx + 1;
    }
  }
  return bestIdx === -1 ? trimmed : trimmed.substring(0, bestIdx + 1).trim();
}

export class AICoach {
  constructor(config) {
    // Accept either a plain API key string (legacy) or a config object
    if (typeof config === 'string') {
      config = { provider: 'openai', apiKey: config };
    }

    this.mode = config.mode || 'direct';
    this.deployment = config.deployment || 'gpt-4o-mini';

    if (this.mode === 'proxy') {
      // Proxy mode: all AI calls go through the backend API (API key never in client)
      this.proxyUrl = config.proxyUrl || '/api/chat';
      this.stravaToken = config.stravaToken;
    } else {
      // Direct mode: call OpenAI/Azure OpenAI directly from the browser (legacy)
      this.provider = config.provider || 'openai';

      if (this.provider === 'azure') {
        this.client = new AzureOpenAI({
          endpoint: config.endpoint,
          apiKey: config.apiKey,
          apiVersion: config.apiVersion || '2025-01-01-preview',
          deployment: this.deployment,
          dangerouslyAllowBrowser: true
        });
      } else {
        this.client = new OpenAI({
          apiKey: config.apiKey,
          dangerouslyAllowBrowser: true
        });
      }
    }

    this.systemPrompt = `You are speaking directly to an athlete. Answer their question first. Do not narrate your analysis, race calculations, or reasoning process. Do not use headers or bullet lists unless the user explicitly asked for a structured breakdown.

You are an expert triathlon coach with 15+ years of experience coaching athletes from beginners to elite level.
    You provide personalized training advice, analyze workout data, and help athletes achieve their goals.

    SAFETY — THESE OVERRIDE EVERYTHING ELSE BELOW:
    You are a coach, not a clinician. You never diagnose, and you never give medical treatment advice. When any of the following appear, lead with the safety response — do not bury it under training advice, and do not soften it:
    - CARDIAC / RED-FLAG SYMPTOMS: chest pain, chest tightness or pressure, unexplained breathlessness, palpitations, fainting, dizziness on exertion, or pain radiating to arm/jaw/back. Tell them to STOP exercising and seek urgent medical assessment. Do NOT prescribe any session, and do NOT tell them when they can resume — that is the clinician's call.
    - HEAD IMPACT / SUSPECTED CONCUSSION: any crash or blow to the head, even if they feel fine. Do not clear them to train. Advise concussion caution and medical assessment, and flag red flags (worsening headache, vomiting, confusion, drowsiness) as urgent.
    - PERSISTENT OR WORSENING PAIN / INJURY: pain lasting more than 7-10 days, worsening pain, swelling, instability, or an altered gait. Refer to a physio or clinician. Do NOT prescribe a rehab protocol or specific rehab exercises — that requires a diagnosis you cannot make.
    - UNDER-FUELLING / RED-S: very low energy intake for the training load, rapid or driven weight loss, missed or absent periods, or a request to lose weight while already restricting. Do NOT provide a weight-loss plan, a calorie deficit, or "eat less" advice. Name the under-fuelling / RED-S risk plainly and recommend a sports dietitian or doctor.
    - MENTAL HEALTH: hopelessness, persistent low mood, inability to cope, or any mention of self-harm. Respond with care, encourage real-world support (GP, therapist, or a crisis line), and do NOT reframe it as a motivation problem or answer it with a training plan.
    - UNDER-18 ATHLETES: do not build adult ultra-endurance or Ironman/70.3 programmes. Raise age-eligibility rules, growth and development, and parent/qualified-coach involvement, and steer toward age-appropriate goals.

    DATA HONESTY — NEVER INVENT THE ATHLETE'S HISTORY:
    Only state facts that appear in the athlete data, coaching memory, or activity list provided to you in this conversation. If you are asked about a race, result, metric, or time period you have not been given, SAY YOU DO NOT HAVE IT and state what you would need. Never fabricate a race the athlete did not do, never invent a past result, and never state a specific metric (FTP, TSB, CTL, threshold, pace) unless that exact number is in the data you were given. An invented number or race destroys trust instantly — "I don't have that" is always the better answer.

    SCOPE AND AMBIGUITY:
    - You only help with endurance training, racing, recovery, nutrition for performance, and related coaching. If asked for something outside that (writing code, general trivia, unrelated tasks), briefly decline and offer to help with their training instead.
    - If a request to change their plan or calendar is ambiguous (no sport, no day, or no clear intent), ASK ONE clarifying question instead of guessing. Do not invent a session the athlete did not ask for.
    - If the message is empty, nonsense, or unintelligible, simply ask what they meant. Do not produce an analysis or a recommendation from nothing.

    STRENGTH & CONDITIONING (check every session):
    Strength is a CORE discipline — not optional. Year-round target: 1-2 sessions/week (2-3 in Base, 1 in Build/Peak, none in Race week). Strength is NOT tracked by CTL/ATL/TSB — you MUST manually count workouts with type "strength" in the recent workout list. If zero or below the phase target in the last 7-14 days: FLAG IT with a specific action. Name 2-3 Essential 6 exercises most relevant to this athlete's disciplines and any flagged injuries: split squat, single-leg RDL, push-up, inverted row, side plank, calf raise. Do not gloss over a strength gap.

    Your coaching philosophy:
    - Individual adaptation over rigid programming - every athlete is unique
    - 80/20 training principle (80% easy aerobic, 20% moderate to hard intensity)
    - Consistency beats perfection - sustainable training leads to long-term success
    - Recovery is part of training, not time off - it's when adaptation happens
    - Technique before intensity - master form before adding speed or power
    - Progressive overload with planned recovery weeks
    - Mental training is as important as physical training

    When providing coaching advice:
    - Be supportive, encouraging, and educational
    - Give specific, actionable recommendations backed by the athlete's actual data
    - Identify concrete patterns in the workout data (e.g. training load trends, fatigue signals, discipline imbalances)
    - Explain the "why" behind your recommendations
    - Reference specific workouts or metrics when relevant
    - Ask clarifying questions when needed

    COACHING MEMORY CORRECTIONS:
    If the athlete tells you something in their stored coaching memory is wrong (e.g. a race they did not actually do, an out-of-date injury, a wrong goal), you MUST direct them to fix it themselves. Do NOT say "ignore it", "I'll keep that in mind", or pretend you can update it — you cannot edit or delete stored memory items. Tell them: "You can remove that yourself in Settings → Coaching Memory — each saved race and injury has an ✕ button next to it. Once it's gone from there it will also be removed from your next coaching report." Keep the response brief and practical.

    If workout data is provided, deeply analyse it for:
    - Training load and intensity distribution across disciplines
    - Weekly volume trends and consistency
    - Recovery patterns and signs of fatigue or overtraining
    - Performance trends (pace, power, heart rate drift)
    - Discipline balance (swim/bike/run ratio)
    - Acute vs chronic workload ratio signals
    - Strength & conditioning frequency (see STRENGTH section above)

    PROPOSING CHANGES TO THE CALENDAR OR TRAINING PLAN:
    The athlete reviews and CONFIRMS every change you propose before it touches their calendar or plan. You never mutate state directly. To propose changes, append ONE fenced \`coach-actions\` JSON block at the very END of your response (after all prose). The app renders each proposal as a card with Confirm / Discard buttons.

    Rules:
    - At most ONE \`coach-actions\` block per response. Place it at the END.
    - The fence label MUST be exactly \`coach-actions\` (lower-case, hyphenated). No other label is parsed — not \`json\`, not \`action\`, not no-label, not plain inline JSON.
    - Each proposal needs a one-line \`summary\` the athlete will read on the card.
    - Use ISO dates (YYYY-MM-DD). Duration in minutes (integer).
    - For \`remove_workouts\`, \`modify_phase\`, \`remove_phase\`, \`remove_milestone\`: use the EXACT \`id\` shown in the calendar / plan context below. Never invent IDs. If the ID you need isn't in the context, ask the athlete instead of guessing.
    - \`remove_workouts\` MUST carry a non-empty \`workoutIds\` array of those exact IDs (e.g. \`"workoutIds": ["workout_123_0_ab", "workout_123_1_cd"]\`). NEVER emit a \`remove_workouts\` proposal without it — the app rejects it and the athlete sees a confusing "1 proposed change couldn't be shown" error. If you want to clear conflicting sessions but cannot match their IDs in the context dump, do NOT emit \`remove_workouts\`: instead name those sessions in prose and let the athlete clear them, or simply add the new sessions and mention the overlap. Adding new workouts without first clearing the old is fine — never block an \`add_workouts\` on a removal you can't express.
    - For \`add_workouts\`, \`add_phase\`, and \`add_milestone\`: do NOT include any \`id\` field — the app generates one at confirm time.
    - If you are unsure whether the athlete wants the change, ask in prose and omit the block.
    - If you describe changes in prose but the user only wants advice, omit the block — proposals are for actionable changes only.
    - If a request can't be met with one of the allowed action types below, explain in prose and omit the block. NEVER invent new action types (e.g. \`create_plan\`, \`change_plan\`, \`update_plan\`, \`set_plan\` — these will be rejected silently and nothing will happen).

    Allowed action types (the ONLY types that exist):
    - \`add_workouts\`         — add one or more sessions to the calendar. \`items[]\` may span multiple weeks in a single proposal.
    - \`remove_workouts\`      — remove sessions from the calendar. Shape: \`{ "type": "remove_workouts", "summary": "...", "workoutIds": ["<exact id>", ...] }\` — the \`workoutIds\` array is REQUIRED and must be non-empty.
    - \`add_phase\`            — add ONE season phase (base/build/peak/taper/race)
    - \`modify_phase\`         — change one field on an existing season phase
    - \`remove_phase\`         — delete a season phase by ID
    - \`add_milestone\`        — add one milestone (e.g. FTP test, race-sim)
    - \`remove_milestone\`     — delete a milestone by ID

    Building a multi-week training plan: compose it as multiple proposals in ONE \`coach-actions\` block — typically a few \`add_phase\` proposals (one per phase), an \`add_workouts\` proposal whose \`items[]\` array spans every week, and any \`add_milestone\` proposals for races / FTP tests / taper starts. The athlete confirms each card independently, so they can accept the workouts but reject a phase, etc. There is NO \`set_plan\` or "create_plan" action — building a plan = composing the proposals above.

    Schema (use these field names exactly):

    \`\`\`coach-actions
    {
      "proposals": [
        {
          "type": "add_phase",
          "summary": "Add 3-week Peak block 25 May → 14 Jun",
          "phase": { "name": "Peak", "type": "peak", "startDate": "2026-05-25", "endDate": "2026-06-14", "notes": "Sharpen for Slateman" }
        },
        {
          "type": "add_workouts",
          "summary": "Add 17 workouts across 3 weeks to Slateman",
          "items": [
            { "date": "2026-05-27", "sport": "swim", "title": "Swim Re-Acquisition", "duration": 45, "zone": "Z2", "description": "200m easy, 4x50m drills, 4x100m Z2, 100m cool down" },
            { "date": "2026-05-28", "sport": "run",  "title": "Endurance + Strides",  "duration": 55, "zone": "Z2", "description": "25min easy then 6x20s strides" },
            { "date": "2026-05-29", "sport": "bike", "title": "Sweet Spot",           "duration": 75, "zone": "Z3", "description": "3x10min @ 85-90% FTP" },
            { "date": "2026-05-31", "sport": "brick","title": "Brick Lite",           "duration": 80, "zone": "Z2", "description": "60min bike + 20min easy run off bike" },
            { "date": "2026-06-02", "sport": "swim", "title": "Endurance + Pace",     "duration": 55, "zone": "Z3", "description": "3x300m at 70.3 pace" },
            { "date": "2026-06-04", "sport": "run",  "title": "Tempo Intervals",      "duration": 50, "zone": "Z3", "description": "3x8min @ 10k pace" },
            { "date": "2026-06-06", "sport": "bike", "title": "Long Ride + Race Pace","duration": 135,"zone": "Z2", "description": "2h15 with 3x8min at race power" },
            { "date": "2026-06-09", "sport": "bike", "title": "Race Sim Intervals",   "duration": 60, "zone": "Z3", "description": "2x15min sweet spot" },
            { "date": "2026-06-13", "sport": "race", "title": "Slateman Standard",    "duration": 180,"zone": "",    "description": "Race day" }
          ]
        },
        {
          "type": "add_milestone",
          "summary": "Slateman race day 13 June",
          "milestone": { "date": "2026-06-13", "type": "race", "label": "Slateman Standard" }
        }
      ]
    }
    \`\`\`

    Valid \`sport\` values: swim, bike, run, strength, brick, other.
    Valid phase \`type\` values: base, build, peak, taper, race.
    Valid milestone \`type\` values: ftp_test, race_simulation, taper_start, race, key_workout, rest_week.

    What NOT to do (these will be IGNORED — no calendar/plan update will happen):
    - ❌ Describing workouts in prose only, no block — "Mon: easy run. Tue: bike." → invisible to the app.
    - ❌ Emitting JSON without a fenced \`coach-actions\` code block — raw inline JSON, \`Proposed Action: {...}\`, or a \`json\`/\`ai-action\`/unlabelled fence will all be ignored. The fence label \`coach-actions\` is the contract.
    - ❌ A workout-plan, workout-delete, or training-plan-update fenced block — those formats are deprecated. Use \`coach-actions\` only.
    - ❌ YAML, bullet lists, markdown tables as the action payload — only the JSON envelope above is parsed.
    - ❌ Inventing action types like \`create_plan\`, \`change_plan\`, \`update_plan\`, \`set_plan\`, \`schedule\`. The seven types above are the only valid types — anything else will be rejected.
    - ❌ Inventing workout/phase/milestone IDs that aren't in the context dump — the proposal will be rejected as stale.
    - ❌ Multiple \`coach-actions\` blocks in one reply — only the last one is read; earlier ones are dropped.

    When the athlete asks "what should I do this week?", "schedule workouts", "clean up duplicates", "add a session", "shift my taper", "populate my plan", "fill in workouts for the next N weeks", "build me a plan to Race X", or similar tactical or strategic changes — propose them as ONE \`coach-actions\` block (composed of however many proposals are needed). Keep the surrounding prose minimal; the cards carry the detail.

    ⚠️ HARD RULE — DO NOT ASK FOR CONFIRMATION IN PROSE:
    The athlete confirms or rejects each proposal by clicking Confirm or Discard on the card. Your prose MUST NOT end with questions like "Do these look good?", "Shall I add these?", "Sound good?" — the card buttons are the only mechanism.

    The correct shape of an "add workouts" response:
    1. Brief prose explaining WHY (1-3 sentences max).
    2. The \`coach-actions\` block with the proposed workouts.
    3. STOP. No "what do you think?", no "let me know if…".

    NOTE: For most users (Commit D onward), proposal-bearing requests are
    handled by a separate structured-output path that enforces format at the
    decoding layer. The instructions above remain as a fallback for the
    rare advice/proposal mis-classification or escape-hatch use of this path.

    RESPONSE FORMATTING:
    When your response covers 2 or more distinct topics or sections, structure it using \`##\` headings (e.g., \`## Overview\`, \`## Recommendations\`, \`## Key Observations\`). The app UI renders these as collapsible sections.`;
  }

  // Internal: call AI via proxy or SDK.
  // Retries up to 3 times on 500 or network errors to handle Azure Function cold-start failures.
  // Progressive backoff: 3s → 6s between attempts. Per-attempt timeout: 60s.
  //
  // `responseFormat` (optional): forwarded to the OpenAI SDK as
  // `response_format` if provided. Used by the structured-output path
  // (generateStructuredResponse) to attach a JSON schema. Server-side
  // pass-through landed in Commit A.
  async _callCompletion(messages, maxTokens, temperature, responseFormat, _attempt = 0) {
    if (this.mode === 'proxy') {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000);

      const body = { messages, maxTokens, temperature };
      if (responseFormat) body.responseFormat = responseFormat;

      let res;
      try {
        res = await fetch(this.proxyUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${this.stravaToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(body),
          signal: controller.signal
        });
      } catch (fetchErr) {
        // Network error or timeout — retry with backoff (max 3 attempts total)
        if (_attempt < 2) {
          await new Promise(resolve => setTimeout(resolve, _attempt === 0 ? 3000 : 6000));
          return this._callCompletion(messages, maxTokens, temperature, responseFormat, _attempt + 1);
        }
        throw fetchErr;
      } finally {
        clearTimeout(timeoutId);
      }

      if (!res.ok) {
        // Retry on 500 (cold start) with progressive backoff — max 3 attempts
        if (res.status === 500 && _attempt < 2) {
          await new Promise(resolve => setTimeout(resolve, _attempt === 0 ? 3000 : 6000));
          return this._callCompletion(messages, maxTokens, temperature, responseFormat, _attempt + 1);
        }
        const errData = await res.json().catch(() => ({}));
        const message = errData.detail
          ? `${errData.error}: ${errData.detail}`
          : (errData.error || `HTTP ${res.status}`);
        const err = new Error(message);
        err.status = res.status;
        throw err;
      }
      const data = await res.json();
      return data.content;
    } else {
      const response = await this.client.chat.completions.create({
        model: this.deployment,
        messages,
        max_tokens: maxTokens,
        temperature,
        ...(responseFormat ? { response_format: responseFormat } : {})
      });
      return response.choices[0].message.content;
    }
  }

  // Streaming variant of _callCompletion. Sends stream:true to the proxy,
  // parses SSE events, and calls onChunk(delta) for each text fragment.
  // Returns the full accumulated text. Falls back to non-streaming on error.
  async _callCompletionStream(messages, maxTokens, temperature, onChunk) {
    if (this.mode !== 'proxy') {
      // Direct mode: no streaming support — fall back to non-streaming
      return this._callCompletion(messages, maxTokens, temperature);
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120000);

    let res;
    try {
      res = await fetch(this.proxyUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.stravaToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ messages, maxTokens, temperature, stream: true }),
        signal: controller.signal
      });
    } catch (fetchErr) {
      clearTimeout(timeoutId);
      // On network failure, fall back to non-streaming with retry logic
      return this._callCompletion(messages, maxTokens, temperature);
    }

    if (!res.ok) {
      clearTimeout(timeoutId);
      // On error, fall back to non-streaming (which has retry logic)
      return this._callCompletion(messages, maxTokens, temperature);
    }

    try {
      const contentType = res.headers.get('content-type') || '';

      if (contentType.includes('text/event-stream')) {
        // True SSE response — parse events from the stream
        return await this._parseSSEStream(res, onChunk);
      }

      if (contentType.includes('application/json')) {
        // Backend returned JSON instead of SSE (e.g. non-streaming fallback)
        const data = await res.json();
        const content = data.content || '';
        if (content && onChunk) {
          // Progressive reveal: emit chunks word-by-word for natural animation
          await this._progressiveReveal(content, onChunk);
        }
        return content;
      }

      // Unknown content type — try reading as text and parsing SSE
      const text = await res.text();
      if (text.startsWith('data:')) {
        return this._parseSSEText(text, onChunk);
      }
      // Last resort: treat as plain content
      if (onChunk) onChunk(text);
      return text;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  // Parse SSE events from a ReadableStream response
  async _parseSSEStream(res, onChunk) {
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let accumulated = '';
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const payload = line.slice(6).trim();
        if (payload === '[DONE]') return accumulated;

        try {
          const { delta } = JSON.parse(payload);
          if (delta) {
            accumulated += delta;
            if (onChunk) onChunk(delta);
          }
        } catch {
          // skip malformed event
        }
      }
    }

    return accumulated;
  }

  // Parse SSE from a complete text string (when response was buffered)
  _parseSSEText(text, onChunk) {
    let accumulated = '';
    const lines = text.split('\n');

    // Collect all deltas first
    const deltas = [];
    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      const payload = line.slice(6).trim();
      if (payload === '[DONE]') break;
      try {
        const { delta } = JSON.parse(payload);
        if (delta) deltas.push(delta);
      } catch {
        // skip malformed
      }
    }

    // Emit all deltas (response was buffered, so deliver immediately)
    for (const delta of deltas) {
      accumulated += delta;
      if (onChunk) onChunk(delta);
    }
    return accumulated;
  }

  // Progressive word-by-word reveal for non-SSE responses
  async _progressiveReveal(content, onChunk) {
    const words = content.split(/(\s+)/);
    for (const word of words) {
      onChunk(word);
      // ~30ms per token creates a natural typing speed
      await new Promise(r => setTimeout(r, 30));
    }
  }

  // R2: Tool-calling variant of _callCompletion. Sends a toolProfile name to
  // the proxy (server maps it to the allowed tools array). Returns the full
  // message object { content, tool_calls } rather than a plain string.
  // Non-streaming only — tool-call streaming requires delta.tool_calls[]
  // accumulation which is a separate future phase.
  async _callCompletionWithTools(messages, maxTokens, toolProfile, _attempt = 0) {
    if (this.mode === 'proxy') {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000);

      let res;
      try {
        res = await fetch(this.proxyUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${this.stravaToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ messages, maxTokens, temperature: 0.6, toolProfile }),
          signal: controller.signal
        });
      } catch (fetchErr) {
        // Do NOT retry a timeout/abort — the 60s budget already elapsed, so a
        // retry would just stack another full-length wait on top.
        if (fetchErr.name !== 'AbortError' && _attempt < 2) {
          await new Promise(resolve => setTimeout(resolve, _attempt === 0 ? 3000 : 6000));
          return this._callCompletionWithTools(messages, maxTokens, toolProfile, _attempt + 1);
        }
        throw fetchErr;
      } finally {
        clearTimeout(timeoutId);
      }

      if (!res.ok) {
        if (res.status === 500 && _attempt < 2) {
          await new Promise(resolve => setTimeout(resolve, _attempt === 0 ? 3000 : 6000));
          return this._callCompletionWithTools(messages, maxTokens, toolProfile, _attempt + 1);
        }
        const errData = await res.json().catch(() => ({}));
        const message = errData.detail
          ? `${errData.error}: ${errData.detail}`
          : (errData.error || `HTTP ${res.status}`);
        const err = new Error(message);
        err.status = res.status;
        throw err;
      }

      const data = await res.json();
      return { content: data.content || '', tool_calls: data.tool_calls || [] };
    } else {
      // Direct mode (legacy/test) — not supported for tool calling
      throw new Error('_callCompletionWithTools requires proxy mode');
    }
  }

  async generateResponse(userMessage, athleteContext = {}, conversationHistory = [], responseMode = null, intent = null) {
    try {
      // Always use 6000 for chat — the coach-actions envelope can come from any
      // request (add a session, clean duplicates, shift taper), not just full
      // plan builds. Truncating the trailing JSON block silently breaks the
      // proposal pipeline. The prose budget rules in the system prompt do the
      // real work of keeping responses focused; this is just headroom for
      // proposal-heavy responses (3-week populate => up to ~20 proposals).
      // Cost impact is negligible since we only pay for tokens actually emitted.
      const maxTokens = 6000;
      // For assistant messages, use the prose-stripped `content` (already has any
      // coach-actions block removed) and append outcome lines so the model can see
      // which of its prior proposals the athlete confirmed / dismissed / left pending.
      const historyMessages = conversationHistory
        .slice(1)       // skip the welcome message
        .slice(-20)     // cap at last 20 messages to stay within token budget
        .map(m => ({
          role: m.type === 'user' ? 'user' : 'assistant',
          content: this.serializeAssistantHistory(m)
        }));

      const sportFocus = athleteContext?.athleteProfile?.background?.sportFocus || 'triathlon';
      const sportLabel = {
        triathlon: 'triathlon',
        duathlon: 'duathlon (run + bike + run)',
        aquabike: 'aquabike (swim + bike)',
        run: 'running',
        bike: 'cycling',
        swim: 'swimming'
      }[sportFocus] || 'triathlon';

      const sportPromptOverride = sportFocus !== 'triathlon'
        ? `\n\nIMPORTANT: This athlete's focus is ${sportLabel}, not triathlon. Tailor ALL advice, plans, and analysis to ${sportLabel} specifically. Only reference disciplines relevant to ${sportLabel}.`
        : '';

      const styleInstructions = {
        direct:       '\n\nCOACH STYLE: Be direct and honest. Prioritise what needs improvement. Do not sugarcoat — if training is inconsistent, volume is low, or there are clear weaknesses, say so plainly and constructively. Skip excessive praise for ordinary work.',
        balanced:     '',
        motivational: '\n\nCOACH STYLE: Be warm, high-energy, and motivational. Celebrate effort and progress genuinely. Frame challenges as opportunities. Keep the athlete feeling capable and fired up, while still being accurate and helpful.'
      };
      const styleOverride = styleInstructions[athleteContext?.coachStyle] ?? '';

      const now = new Date();
      const todayISO = now.toISOString().slice(0, 10);
      const todayReadable = now.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
      const dateContext = `TODAY'S DATE: ${todayReadable} (${todayISO}). Use this as the definitive reference for today, tomorrow, yesterday, and all relative date calculations. Do not use any other date as "today".`;
      const directPreamble = responseMode === 'direct'
        ? 'Answer this question in 2–3 sentences. Lead with the recommendation. No reasoning headers, no bullet lists.\n\n'
        : '';
      const messages = [
        { role: 'system', content: `${directPreamble}${dateContext}\n\n${this.systemPrompt}${sportPromptOverride}${styleOverride}` },
        ...historyMessages,
        { role: 'user', content: this.formatUserInput(userMessage, athleteContext, intent) }
      ];

      return await this._callCompletion(messages, maxTokens, 0.6, undefined);
    } catch (error) {
      console.error('AI response error:', error);
      throw error;
    }
  }

  // Streaming variant of generateResponse. Calls onChunk(delta) for each text
  // fragment as it arrives. Returns the full accumulated response text.
  // Falls back to non-streaming generateResponse if streaming fails.
  async generateResponseStream(userMessage, athleteContext = {}, conversationHistory = [], onChunk, responseMode = null, intent = null) {
    try {
      // 6000 — matches generateResponse(). Same rationale: keep the trailing
      // coach-actions envelope from being truncated on proposal-heavy responses.
      const maxTokens = 6000;

      const historyMessages = conversationHistory
        .slice(1)
        .slice(-20)
        .map(m => ({
          role: m.type === 'user' ? 'user' : 'assistant',
          content: this.serializeAssistantHistory(m)
        }));

      const sportFocus = athleteContext?.athleteProfile?.background?.sportFocus || 'triathlon';
      const sportLabel = {
        triathlon: 'triathlon',
        duathlon: 'duathlon (run + bike + run)',
        aquabike: 'aquabike (swim + bike)',
        run: 'running',
        bike: 'cycling',
        swim: 'swimming'
      }[sportFocus] || 'triathlon';

      const sportPromptOverride = sportFocus !== 'triathlon'
        ? `\n\nIMPORTANT: This athlete's focus is ${sportLabel}, not triathlon. Tailor ALL advice, plans, and analysis to ${sportLabel} specifically. Only reference disciplines relevant to ${sportLabel}.`
        : '';

      const styleInstructions = {
        direct:       '\n\nCOACH STYLE: Be direct and honest. Prioritise what needs improvement. Do not sugarcoat — if training is inconsistent, volume is low, or there are clear weaknesses, say so plainly and constructively. Skip excessive praise for ordinary work.',
        balanced:     '',
        motivational: '\n\nCOACH STYLE: Be warm, high-energy, and motivational. Celebrate effort and progress genuinely. Frame challenges as opportunities. Keep the athlete feeling capable and fired up, while still being accurate and helpful.'
      };
      const styleOverride = styleInstructions[athleteContext?.coachStyle] ?? '';

      const now = new Date();
      const todayISO = now.toISOString().slice(0, 10);
      const todayReadable = now.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
      const dateContext = `TODAY'S DATE: ${todayReadable} (${todayISO}). Use this as the definitive reference for today, tomorrow, yesterday, and all relative date calculations. Do not use any other date as "today".`;
      const directPreamble = responseMode === 'direct'
        ? 'Answer this question in 2–3 sentences. Lead with the recommendation. No reasoning headers, no bullet lists.\n\n'
        : '';
      const messages = [
        { role: 'system', content: `${directPreamble}${dateContext}\n\n${this.systemPrompt}${sportPromptOverride}${styleOverride}` },
        ...historyMessages,
        { role: 'user', content: this.formatUserInput(userMessage, athleteContext, intent) }
      ];

      return await this._callCompletionStream(messages, maxTokens, 0.6, onChunk);
    } catch (error) {
      console.error('AI streaming error, falling back to non-streaming:', error);
      return this.generateResponse(userMessage, athleteContext, conversationHistory, responseMode, intent);
    }
  }

  // Classify an incoming user message to decide which AI path handles it.
  // Returns 'proposal_bearing' (use generateStructuredResponse) or 'advice'
  // (use generateResponseStream / generateResponse — the prose path).
  //
  // Used by App.js to dispatch when REACT_APP_USE_STRUCTURED_COACH_ACTIONS
  // is enabled. Conservative on the structured-path side — false negatives
  // (a proposal-bearing request goes to prose) just hit the old behaviour;
  // false positives (advice asks go to structured) get an envelope with
  // proposals: [] and a summary_message that handles it fine.
  //
  // Keep this in sync with the action-intent retry regex in App.js — the
  // two are solving the same "did the user ask for a change" question.
  classifyIntent(userMessage) {
    if (typeof userMessage !== 'string' || !userMessage.trim()) return 'advice';

    // Three intents in priority order:
    //   1. 'plan_build'      — build/create/design/set-up the plan STRUCTURE.
    //                          Routes to generatePhaseSkeleton which uses
    //                          a constrained schema (add_phase / milestone
    //                          only — workouts forbidden by the schema).
    //   2. 'proposal_bearing'— other action verbs (add a workout, remove
    //                          duplicates, modify a phase, etc). Routes
    //                          to the existing generic structured path.
    //   3. 'advice'          — everything else. Routes to the prose path.
    //
    // Detection sequence matters: plan_build is checked first because
    // its verbs ("build", "create", "populate", "update") are a STRICT
    // SUBSET of the generic ACTION_VERBS list. Routing it through the
    // generic path is the failure mode we just exited (the AI took the
    // shortcut and dumped everything as add_workouts).

    // Phrase shape: VERB + (optional "me") + (optional article a/the/my)
    // + (optional modifier "training/race/season") + plan-noun. Anchored
    // with \b on the verb stems. Plan-noun is "plan/season/programme/
    // program/structure/schedule" so phrasings like "set up my season"
    // or "build me a training schedule" all match.
    // Pattern: verb-stem + filler (any 0-5 words) + plan-noun-stem.
    //
    // `\w*` after each stem so inflections match on both sides:
    //   verb side  — "create"/"updated"/"populating"
    //   noun side  — "plans"/"programmes"/"structures"/"scheduled"
    //
    // The filler `(?:[\w']+\s+){0,5}` absorbs anything plausible between
    // the verb and the noun ("me", "a", "the", "my", "some", "a few",
    // "training", "the next 12 weeks", etc.) without enumerating every
    // quantifier and modifier individually. False positives ("build a
    // cabin near the plan") are accepted as a deliberate trade — they
    // route to the constrained schema, which produces a phase-only
    // response and is harmless if it wasn't actually a plan request.
    // False negatives broke the user's workflow today (Bug 1, 2026-05-28).
    const PLAN_BUILD = /\b(build|creat|design|set\s+up|rebuild|populat|updat)\w*\s+(?:[\w']+\s+){0,5}(plan|season|programme|program|structure|schedule)\w*\b/i;
    if (PLAN_BUILD.test(userMessage)) return 'plan_build';

    // Short bounded life-event with date context → proposal_bearing.
    // Catches "I'm hiking June 3-8, it'll be a recovery/tapering week" which
    // contains no explicit action verb but clearly implies targeted calendar
    // and/or plan adjustments for those specific dates.
    const TRAVEL_CONSTRAINT = /\b(hiking|hike|hikes|trekking|trek|travelling|traveling|travel|away|holiday|vacation|trip|recovery\s+week|tapering?\s+week|rest\s+week|off\s+week)\b/i;
    const HAS_DATE_CONTEXT = /\b(\d+(?:st|nd|rd|th)?|jan\w*|feb\w*|mar\w*|apr\w*|may|jun\w*|jul\w*|aug\w*|sep\w*|oct\w*|nov\w*|dec\w*|monday|tuesday|wednesday|thursday|friday|saturday|sunday|next\s+week|this\s+week)\b/i;
    if (TRAVEL_CONSTRAINT.test(userMessage) && HAS_DATE_CONTEXT.test(userMessage)) return 'proposal_bearing';

    // Same stem-prefix rationale as before for the generic action verbs.
    // False positives ("additional" via "add", "movie" via "mov") are
    // safer than false negatives — false-positive advice queries get an
    // empty proposals[] response and render as plain prose; false-
    // negative action queries get no actionable cards.
    const ACTION_VERBS = /\b(add|schedul|creat|buil|remov|delet|clean|shift|chang|mov|cancel|swap|replac|fill|reschedul|populat|generat|updat|modif|edit|revis|adjust|tweak|make\s+me|give\s+me|plan\s+(me|out|my)|set\s+up\s+my)/i;
    return ACTION_VERBS.test(userMessage) ? 'proposal_bearing' : 'advice';
  }

  // LLM-based intent classifier (R1). Makes a single short structured-output
  // call (max 20 tokens, temperature 0) and returns one of the three intent
  // labels. Throws on network error or invalid response — caller must provide
  // a fallback (see App.js handleSendMessage).
  async classifyIntentLLM(userMessage) {
    if (typeof userMessage !== 'string' || !userMessage.trim()) return 'advice';
    const messages = [
      { role: 'system', content: INTENT_CLASSIFIER_PROMPT },
      { role: 'user',   content: userMessage },
    ];
    const raw = await this._callCompletion(messages, 20, 0.0, INTENT_CLASSIFIER_SCHEMA);
    const parsed = JSON.parse(raw);
    const VALID = ['plan_build', 'proposal_bearing', 'populate_workouts', 'coaching_question', 'coaching_analysis', 'advice'];
    if (!VALID.includes(parsed.intent)) {
      throw new Error(`[R1] Unexpected intent label: ${parsed.intent}`);
    }
    return parsed.intent;
  }

  // System prompt for the STRUCTURED-OUTPUT path. Far shorter than the
  // prose-path systemPrompt because the schema enforces format at the
  // token level — no need to repeat "use coach-actions block", "don't
  // ask permission", "no markdown table duplication", etc. That's all
  // the API's job now.
  //
  // What remains in this prompt is the behavioural nuance the schema
  // CANNOT enforce: coaching domain knowledge, when to emit zero
  // proposals (advice / clarification), and quality calibration for
  // proposal content.
  _buildStructuredSystemPrompt({ dateContext, sportOverride, styleOverride }) {
    return `${dateContext}

You are an expert triathlon coach with 15+ years of experience coaching athletes from beginners to elite level. The athlete reviews and CONFIRMS every change you propose via a card UI — you never mutate state directly.

Your response is a structured envelope:
- summary_message: short coaching message (<=4 short sentences) shown to the athlete above the proposal cards. Explain the WHY of what you're proposing. NEVER list specific workouts here — the cards already display every workout's date, sport, title, duration, zone, and description. Duplication wastes the athlete's reading time.
- proposals: zero or more changes. Each renders as a Confirm/Discard card.

When to emit ZERO proposals:
- The athlete asks for advice or analysis ("how should I pace?", "why am I tired?", "what does my data say?")
- The request is too vague — ask a clarifying question in summary_message
- You lack the data to recommend specifically — say what you'd need

When to emit proposals:
- Athlete asks to add/schedule/populate/build/create workouts, phases, or milestones → propose them
- Athlete asks to remove/delete/clean up specific things → propose removals
- Athlete asks to shift/modify a phase → propose modify_phase
The card UI is the confirmation mechanism. Don't ask "Shall I proceed?" — emit the proposals.

How to compose plan creation:
- For a multi-week populate request, emit ONE add_workouts proposal per week (decomposed by week so the athlete can confirm/discard week-by-week).
- If the plan structure is new, pair with add_phase proposals for each macro block (base/build/peak/taper/race).
- Add add_milestone proposals for race days, FTP tests, taper starts.
- Use ISO dates. Snap your dates to calendar weeks (Mon-Sun); the app re-snaps but cleaner if you do.

⚠️ HARD RULE — PLAN-BUILD REQUESTS REQUIRE add_phase PROPOSALS:
When the athlete asks to BUILD / CREATE / SET UP / DESIGN / POPULATE / UPDATE a training plan (any phrasing that implies creating the season/race structure, not just adding individual sessions), your response MUST include add_phase proposals. add_workouts proposals on their own are NOT sufficient and will not appear on the Plan page — they only land on the Calendar. The Plan page is driven exclusively by add_phase and add_milestone proposals. If you describe phases in the summary_message ("structured into Base, Build, Peak, Taper phases") but emit zero add_phase proposals, the user sees zero updates on the Plan page and your prose is a lie. Emit the add_phase proposals matching whatever phase structure you described — one proposal per phase, with the type field set to one of: base / build / peak / taper / race.

⚠️ HARD RULE — MULTI-RACE PLANS:
When the athlete has multiple races, EACH race gets its OWN ordered phase sequence sized to its priority and time-to-race (typically: Base → Build → Peak → Taper, possibly plus a short race-week phase). NEVER collapse two races into one combined "Race & Peak" mega-phase. NEVER emit a single phase whose date range spans both races. A common mistake is to create one giant phase covering the entire window from "now" to "the further race" — DO NOT do this. Instead: phases for race 1 occupy weeks before race 1, then phases for race 2 occupy the weeks between race 1 and race 2. If race 1 is a B-race tune-up for race 2 (A-race), it's fine for race 1 to have fewer or compressed phases — but it still gets its own phase sequence, not a shared one.

⚠️ HARD RULE — targetRaceId FORMAT:
The targetRaceId field MUST be the race's exact ISO date YYYY-MM-DD as shown in the "Upcoming races" section of the context dump (e.g. "2026-07-05"). NEVER prefix with a race name, NEVER use "race:" or any other label, NEVER invent composite IDs like "Holkham_Half_2026-07-05" or "Slateman_Standard_2026-06-14". The app's race grouping matches against the race date string exactly — any other format silently orphans the phase or milestone into its own bogus group. If the phase isn't tied to any specific race, set targetRaceId to null.

Modifying existing items:
- modify_phase / remove_phase / remove_workouts / remove_milestone require the EXACT id from the context dump below.
- NEVER invent ids. If the id you'd need isn't in the context, ask the athlete in summary_message instead.

Per-type payload rules in the envelope schema:
- Every proposal has seven per-type payload fields. Populate ONLY the one matching the proposal's "type" field. Set the other six to null.
- For modify_phase.changes, leave fields you don't want to change as null. Only fill the fields actually being changed.

Coaching principles (applied to BOTH summary_message tone and proposal content):
- 80/20 (80% easy aerobic, 20% moderate-hard)
- Progressive overload with planned recovery
- Strength training is a CORE discipline (1-2 sessions/week per phase target), not optional. Reference the Essential 6 framework from the knowledge base.
- Individual adaptation over rigid programming

COACHING MEMORY CORRECTIONS: If the athlete tells you something in their stored coaching memory is wrong, tell them they can fix it in Settings → Coaching Memory. Do not say you'll update it — you cannot edit stored memory.${sportOverride}${styleOverride}`;
  }

  // Structured-output path. Sends the coach-actions JSON Schema with
  // response_format strict:true, parses the JSON response, and returns
  // a parsed envelope: { summary_message, proposals }.
  //
  // Differs from generateResponse:
  //   - Different (much shorter) system prompt — schema enforces format
  //   - response_format attached to the SDK call
  //   - Non-streaming (structured outputs stream as partial JSON which
  //     the UI can't progressively render; safer to wait for the
  //     complete envelope)
  //   - Returns parsed object, not raw string
  //
  // Used by App.js when REACT_APP_USE_STRUCTURED_COACH_ACTIONS=true and
  // classifyIntent(userMessage) === 'proposal_bearing'.
  async generateStructuredResponse(userMessage, athleteContext = {}, conversationHistory = [], intent = null) {
    const responseFormat = coachActionsResponseFormat;


    // 6000 — same headroom as prose path. Schema enforces brevity in
    // summary_message; proposals[] is the bulk of the response.
    const maxTokens = 6000;

    const historyMessages = conversationHistory
      .slice(1)
      .slice(-20)
      .map(m => ({
        role: m.type === 'user' ? 'user' : 'assistant',
        // Use the prose-stripped content for assistant turns; outcome
        // lines are still useful so the model sees what was confirmed.
        content: this.serializeAssistantHistory(m),
      }));

    const sportFocus = athleteContext?.athleteProfile?.background?.sportFocus || 'triathlon';
    const { override: sportOverride } = this._getSportContext(sportFocus);

    const styleInstructions = {
      direct:       '\n\nCOACH STYLE: Direct, honest, no sugarcoating. Prioritise what needs improvement.',
      balanced:     '',
      motivational: '\n\nCOACH STYLE: Warm, motivational. Celebrate progress, frame challenges as opportunities.',
    };
    const styleOverride = styleInstructions[athleteContext?.coachStyle] ?? '';

    const now = new Date();
    const todayISO = now.toISOString().slice(0, 10);
    const todayReadable = now.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    const dateContext = `TODAY'S DATE: ${todayReadable} (${todayISO}). Use this as the definitive reference for today, tomorrow, and all relative date calculations.`;

    const messages = [
      { role: 'system', content: this._buildStructuredSystemPrompt({ dateContext, sportOverride, styleOverride }) },
      ...historyMessages,
      { role: 'user', content: this.formatUserInput(userMessage, athleteContext, intent) },
    ];

    const raw = await this._callCompletion(messages, maxTokens, 0.6, responseFormat);

    // Parse — strict mode guarantees JSON, but we still defend against
    // any pathological case (content_filter truncation, network blip).
    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (err) {
      const e = new Error('Structured response was not valid JSON: ' + (err.message || err));
      e.cause = err;
      e.rawContent = raw;
      throw e;
    }

    // Light shape check — schema should guarantee these, but defensive
    // is cheap and the failure mode is clear if we get something weird.
    if (typeof parsed.summary_message !== 'string') {
      const e = new Error('Structured response missing summary_message');
      e.rawContent = raw;
      throw e;
    }
    if (!Array.isArray(parsed.proposals)) {
      const e = new Error('Structured response missing proposals[]');
      e.rawContent = raw;
      throw e;
    }

    return parsed;
  }

  // System prompt for the PHASE-SKELETON path. Used by the dedicated
  // plan-build flow (Commit C2/C3). Far narrower than the generic
  // structured prompt because the model has ONE job here: lay out the
  // macro phase structure. The schema enforces this by construction
  // (no add_workouts in the type enum), so the prompt doesn't need to
  // beg for it — but the prompt DOES carry the periodisation guidance
  // and the canonical-ID reminder that the schema description alone
  // can't enforce.
  _buildPhaseSkeletonSystemPrompt({ dateContext, sportOverride, styleOverride }) {
    return `${dateContext}

You are an expert triathlon coach. The athlete has asked you to build / create / set up / update their training PLAN STRUCTURE. Your job in THIS response is narrow: lay out the macro phase shape (Base / Build / Peak / Taper / Race) and key milestones (race days, FTP tests, taper starts). You will NOT emit individual workouts in this turn — the athlete will request those in a follow-up turn once the phases are confirmed.

Your response is a structured envelope with summary_message + proposals[]. The schema for this path explicitly omits add_workouts and remove_workouts from the allowed action types — you literally cannot emit a workout proposal here, only phase/milestone proposals.

HARD RULES:

1. Emit at least 2 add_phase proposals for any plan-build request. Plans with fewer than 2 phases are not plans. One add_phase per macro block, with type set to one of: base, build, peak, taper, race.

2. Multi-race plans: EACH race gets its OWN ordered phase sequence sized to its priority and time-to-race. NEVER collapse two races into one combined "Race & Peak" mega-phase. NEVER emit a phase whose date range spans both races. Typical pattern for a B-race (tune-up) + A-race (target): a short Build → tune-up race week for race 1, then a Recovery + Build → Peak → Taper sequence for race 2. If race 1 is the A-race (further-out tune-up), invert.

3. targetRaceId for each phase MUST be the race's exact ISO date YYYY-MM-DD as shown in the "Target races" section of the context dump (e.g. "2026-07-05"). NEVER prefix with a race name, NEVER use "race:" or any other label, NEVER invent composite IDs. The schema description says this; the grouping in TrainingPlanPage depends on it.

4. Phase dates: ISO format YYYY-MM-DD. Snap to calendar weeks (Mon start, Sun end); the app re-snaps but cleaner if you do. Phases for the same race must not overlap — each phase ends the week before the next begins.

5. add_milestone proposals: emit at minimum one per race day (with type=race, date matching the race date, label set to the race name) plus a taper_start milestone one week before each race. Other milestones (ftp_test, race_simulation) are encouraged where they fit. targetRaceId follows the same rule as phases — exact ISO date.

6. summary_message:
   - <=4 short sentences explaining the overall periodisation strategy (which race is the A-race, how race 1 fits into the plan, key inflection points).
   - MUST end with this exact sentence: "Once you've confirmed these phases, ask me to populate the workouts."
   - Do NOT list the phases in prose — the cards already display them.

Coaching principles (applied to phase shaping):
- Aerobic base before intensity.
- Build progressively, plan recovery weeks (typical 3:1 loading).
- Taper duration is roughly proportional to race distance: ~3-5 days for sprint/olympic, ~7-10 days for 70.3, ~14-21 for Ironman.
- Strength stays present across phases (1-2 sessions/week per phase target) — but this is workout-level detail, NOT a phase, so don't emit a "Strength" phase.${sportOverride}${styleOverride}`;
  }

  // Phase-skeleton path (Commit C2/C3). Used when classifyIntent returns
  // 'plan_build'. Sends the phaseSkeletonResponseFormat (constrained
  // schema — no add_workouts allowed) and the narrow system prompt
  // above. Returns the parsed envelope.
  //
  // Differs from generateStructuredResponse only in the schema and
  // prompt — the messages array, history, context, and post-parse shape
  // checks are identical. Factored separately for clarity rather than
  // parameterising generateStructuredResponse (the latter is the right
  // refactor when we add a third structured path, e.g. workouts-for-
  // confirmed-phases).
  async generatePhaseSkeleton(userMessage, athleteContext = {}, conversationHistory = [], intent = 'plan_build') {

    const maxTokens = 6000;

    const historyMessages = conversationHistory
      .slice(1)
      .slice(-20)
      .map(m => ({
        role: m.type === 'user' ? 'user' : 'assistant',
        content: this.serializeAssistantHistory(m),
      }));

    const sportFocus = athleteContext?.athleteProfile?.background?.sportFocus || 'triathlon';
    const { override: sportOverride } = this._getSportContext(sportFocus);

    const styleInstructions = {
      direct:       '\n\nCOACH STYLE: Direct, honest, no sugarcoating. Prioritise what needs improvement.',
      balanced:     '',
      motivational: '\n\nCOACH STYLE: Warm, motivational. Celebrate progress, frame challenges as opportunities.',
    };
    const styleOverride = styleInstructions[athleteContext?.coachStyle] ?? '';

    const now = new Date();
    const todayISO = now.toISOString().slice(0, 10);
    const todayReadable = now.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    const dateContext = `TODAY'S DATE: ${todayReadable} (${todayISO}). Use this as the definitive reference for today, tomorrow, and all relative date calculations.`;

    const messages = [
      { role: 'system', content: this._buildPhaseSkeletonSystemPrompt({ dateContext, sportOverride, styleOverride }) },
      ...historyMessages,
      { role: 'user', content: this.formatUserInput(userMessage, athleteContext, intent) },
    ];

    const raw = await this._callCompletion(messages, maxTokens, 0.6, coachActionsPhaseSkeletonFormat);

    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (err) {
      const e = new Error('Phase-skeleton response was not valid JSON: ' + (err.message || err));
      e.cause = err;
      e.rawContent = raw;
      throw e;
    }
    if (typeof parsed.summary_message !== 'string') {
      const e = new Error('Phase-skeleton response missing summary_message');
      e.rawContent = raw;
      throw e;
    }
    if (!Array.isArray(parsed.proposals)) {
      const e = new Error('Phase-skeleton response missing proposals[]');
      e.rawContent = raw;
      throw e;
    }
    return parsed;
  }

  // R2: Unified tool-calling path. Replaces generateStructuredResponse and
  // generatePhaseSkeleton. toolProfile controls which tools the model can emit:
  //   'plan_build'       → phases/milestones only (no workouts)
  //   'proposal_bearing' → all 7 action types
  //   'populate_workouts'→ add/remove workouts only (no phase changes)
  //
  // Returns { content, tool_calls } from the proxy. The client-side
  // dispatchToolCalls() function (coachActions.js) converts tool_calls into
  // the same proposal shape applyProposal already consumes.
  async generateWithTools(userMessage, athleteContext = {}, conversationHistory = [], toolProfile = 'proposal_bearing') {
    const maxTokens = 6000;

    const historyMessages = conversationHistory
      .slice(1)
      .slice(-20)
      .map(m => ({
        role: m.type === 'user' ? 'user' : 'assistant',
        content: this.serializeAssistantHistory(m),
      }));

    const sportFocus = athleteContext?.athleteProfile?.background?.sportFocus || 'triathlon';
    const { override: sportOverride } = this._getSportContext(sportFocus);

    const styleInstructions = {
      direct:       '\n\nCOACH STYLE: Direct, honest, no sugarcoating. Prioritise what needs improvement.',
      balanced:     '',
      motivational: '\n\nCOACH STYLE: Warm, motivational. Celebrate progress, frame challenges as opportunities.',
    };
    const styleOverride = styleInstructions[athleteContext?.coachStyle] ?? '';

    const now = new Date();
    const todayISO = now.toISOString().slice(0, 10);
    const todayReadable = now.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    const dateContext = `TODAY'S DATE: ${todayReadable} (${todayISO}). Use this as the definitive reference for today, tomorrow, and all relative date calculations.`;

    const systemPrompt = this._buildToolCallingSystemPrompt({ dateContext, sportOverride, styleOverride, toolProfile });

    const messages = [
      { role: 'system', content: systemPrompt },
      ...historyMessages,
      { role: 'user', content: this.formatUserInput(userMessage, athleteContext, toolProfile) },
    ];

    return await this._callCompletionWithTools(messages, maxTokens, toolProfile);
  }

  // System prompt for the tool-calling path.
  _buildToolCallingSystemPrompt({ dateContext, sportOverride, styleOverride, toolProfile }) {
    const profileNotes = {
      plan_build: `
YOUR TASK: The athlete wants to BUILD or RESTRUCTURE their training plan at a SEASON level. You have the FULL toolset. Build the STRUCTURE (phases + milestones) AND seed the OPENING workouts so the Plan page and the Calendar are immediately consistent — never leave one surface populated and the other empty.

RULES FOR plan_build:
1. Emit at least 2 add_phase calls for any plan-build request. Plans with fewer than 2 phases are not plans.
2. Multi-race plans: EACH race gets its OWN ordered phase sequence. NEVER collapse two races into one combined mega-phase. NEVER emit a single phase whose date range spans both races.
3. targetRaceId for each phase/milestone MUST be the race's exact ISO date YYYY-MM-DD from the "Target races" section of the context (e.g. "2026-07-05"). Use an empty string if not tied to a race.
4. Never emit a remove_phase call unless the EXACT phaseId is visible in the "Training plan phases" section of the context dump. If that section is empty, do not call remove_phase at all. Only remove existing phases/milestones/workouts when the athlete explicitly asks to clear or replace them — when they just say "build me a plan", ADD, don't remove.
5. add_milestone calls: emit at minimum one per race day (type=race, date matching the race date) plus a taper_start milestone one week before each race.
6. BULK CLEAR: When the athlete asks to remove or clear ALL existing phases, emit ALL remove_phase calls for every visible phaseId in a SINGLE tool_calls array in one response. Do NOT remove one phase per turn. Follow the removes immediately with the add_phase calls for the new plan in the same response.
6b. NEVER END ON REMOVALS ALONE: A "refresh", "rebuild", "redo", or "clean slate" request is NOT complete after you remove things. In the SAME turn (you may be prompted to continue across steps), you MUST also emit the replacement add_phase + add_milestone + the week-1 add_workouts. Removing the old plan without building the new one is a failed response — never stop there.
7. SEED THE OPENING WORKOUTS: After the phases and milestones, emit add_workouts for the FIRST 1–2 weeks of the plan only — at MINIMUM week 1 (one add_workouts call per week), at MOST 2 weeks. Do NOT lay out workouts for the entire season; the athlete populates later blocks in follow-up turns. This prevents an overwhelming flood of cards while keeping the Calendar consistent with the Plan from turn one.
8. PROSE RESPONSE: Maximum 2 sentences — state ONLY what phases you are creating and that you've seeded the first 1–2 weeks. DO NOT write weekly tables, bullet lists, training philosophy sections, TSS targets, or workout breakdowns in prose. All structure goes through tool calls. End with: "I've set up your phases and the opening sessions — ask me to populate the next block when you're ready."`,

      populate_workouts: `
YOUR TASK: The athlete wants workouts populated for EXISTING phases. Use add_workouts (and remove_workouts only if replacing). You have phase/milestone tools available too, but DO NOT call add_phase, modify_phase, remove_phase, add_milestone, or remove_milestone unless the athlete EXPLICITLY asks to change the plan structure, phase dates, or milestones. A workout-fill request must not produce phase-restructuring cards.

RULES FOR populate_workouts:
1. Use ISO dates. Snap sessions to calendar days matching the existing phase date ranges.
2. Apply 80/20 principle: ~80% easy aerobic (Z1-Z2), ~20% threshold/interval (Z3-Z5).
3. For multi-week requests, emit ONE add_workouts call per week so the athlete can confirm week-by-week.
4. Strength stays present (1-2 sessions/week).`,

      proposal_bearing: `
YOUR TASK: Make specific targeted changes the athlete has requested (add/remove/modify workouts, phases, or milestones). Use the tool(s) matching what was asked.

RULES:
1. COMPLETENESS — emit EVERY change in ONE response. A single message often asks for MULTIPLE distinct changes, sometimes across MULTIPLE surfaces (Calendar workouts, Plan phases, milestones). Emit a SEPARATE tool call for EACH distinct change in the SAME response. Never stop after the first tool call when more were requested. Example: "remove this week's sessions and add an easy hike + a swim instead" = one remove_workouts call AND one add_workouts call, together.
2. PROSE MUST MATCH YOUR TOOL CALLS — never describe, promise, or imply a change in your text that you do not also enact with a tool call this turn. If you write "let me add…" or "I'll create…", you MUST emit the matching tool call in the same response. Do not narrate a plan you don't execute.
3. For modifying/removing: only use IDs explicitly present in the context dump. Never invent IDs.
4. For add_workouts: group workouts for the same week into a single call; use a separate add_workouts call per week for multi-week ranges.
5. If a detail is genuinely ambiguous or missing, call request_clarification (see WHEN TO ASK FIRST above) instead of guessing — do not silently skip the change.`,
    };

    return `${dateContext}

You are an expert triathlon coach with 15+ years of experience. The athlete reviews and CONFIRMS every tool call you make via a card UI — you never mutate state directly.

WHEN NOT TO USE TOOLS: If the athlete is only asking a question, seeking advice, or asking for analysis — and is NOT asking you to add, build, change, schedule, or remove anything — answer directly in prose with ZERO tool calls. Do NOT invent a change they did not ask for. Tools are for state changes the athlete requested; plain questions get a plain coaching answer.

WHEN TO ASK FIRST: If the athlete DOES want a change but their request is genuinely ambiguous or under-specified — an unclear date ("next week"), which of several races they mean, a missing duration/distance, or a self-contradictory instruction — call request_clarification with ONE specific question INSTEAD of guessing. Offer 2–4 short, self-contained options when they'd help. Do NOT ask if the request is already clear, and do NOT combine request_clarification with any other tool call in the same turn — ask, then act on the athlete's reply next turn.

COMPLETING A REQUEST ACROSS STEPS: After you emit tool calls they are queued for the athlete and you may be asked to continue. Keep emitting tool calls until the athlete's request is FULLY satisfied across every surface it touches — do not split a single request into "I'll do part now and the rest later". When everything the athlete asked for has been proposed, reply with a brief confirmation and emit NO further tool calls.

${profileNotes[toolProfile] || profileNotes.proposal_bearing}

General coaching principles (applied to all tool call content):
- 80/20 (80% easy aerobic, 20% moderate-hard)
- Progressive overload with planned recovery (typical 3:1 loading weeks)
- Strength training is a CORE discipline (1-2 sessions/week per phase), not optional
- Individual adaptation over rigid programming

For modify_phase and remove_phase/milestone operations: the EXACT ID must be visible in the context dump. If the ID you'd need is not there, ask the athlete instead of guessing.

COACHING MEMORY CORRECTIONS: If the athlete tells you stored coaching memory is wrong, tell them they can fix it in Settings → Coaching Memory. You cannot edit stored memory.${sportOverride}${styleOverride}`;
  }

  // Returns a sport label and system-prompt override based on sportFocus.
  // Used by all AI methods that need to respect a non-triathlon athlete's focus.
  _getSportContext(sportFocus) {
    const label = {
      triathlon: 'triathlon',
      duathlon:  'duathlon (run + bike)',
      aquabike:  'aquabike (swim + bike)',
      run:       'running',
      bike:      'cycling',
      swim:      'swimming',
    }[sportFocus] || 'triathlon';

    const override = sportFocus && sportFocus !== 'triathlon'
      ? `\n\nCRITICAL: This athlete is a ${label} athlete, NOT a triathlete. ALL coaching advice, training plans, analysis, and recommendations MUST be specific to ${label}. NEVER suggest or reference disciplines not in ${label}. Do not mention swim/bike/run balance unless all those disciplines are part of ${label}.`
      : '';

    return { label, override };
  }

  // For each prior message in the conversation, build the string the AI sees.
  // - User messages: their content as-is.
  // - Assistant messages: the prose-stripped `content` (coach-actions block
  //   removed at parse time) plus one outcome line per proposal so the model
  //   knows what the athlete confirmed, dismissed, or left pending. Without
  //   this, the AI would re-propose changes it already made or invent a delete
  //   for a workout the athlete refused. Stale entries are skipped silently.
  serializeAssistantHistory(m) {
    if (m.type === 'user') return m.content || '';
    const base = (m.content || '').trim();
    const proposals = Array.isArray(m.proposals) ? m.proposals : [];
    if (proposals.length === 0) return base;
    const outcomes = proposals.map(p => {
      const label = (p.summary || p.type || 'proposal').trim();
      const statusMap = {
        confirmed: 'CONFIRMED by athlete',
        dismissed: 'DISMISSED by athlete',
        stale:     'STALE (target no longer exists)',
        pending:   'STILL PENDING — remind athlete to confirm the existing card unless they explicitly ask for a revised or new proposal',
      };
      const status = statusMap[p.status] || `STATUS=${p.status}`;
      return `[Proposal: ${p.type} — "${label}" — ${status}]`;
    }).join('\n');
    return base ? `${base}\n\nAthlete proposal outcomes (do not treat as coach advice):\n${outcomes}` : outcomes;
  }

  formatUserInput(message, context, intent = null) {
    // Intent-based context scoping — each intent gets only what it needs:
    //   coaching_question  → last 14 days + summary + memory (conversational, cheap)
    //   proposal_bearing   → last 14 days + calendar/plan IDs (targeted changes)
    //   populate_workouts  → last 14 days + phase dates + calendar (workout scheduling)
    //   plan_build / coaching_analysis / null → full context (current behaviour)
    const isMinimal = intent === 'coaching_question';
    const isIdFocused = intent === 'proposal_bearing' || intent === 'populate_workouts';
    const recentCutoffDate = (isMinimal || isIdFocused)
      ? new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
      : null;

    // AI-initiated briefings (session opener, daily briefing, race prep) call
    // this with an empty message — they want a context dump only, not a
    // chat-shaped prompt. Without this guard, the literal `Athlete message: ""`
    // line confuses the model into hallucinating meta-sections like
    // "Suggested coach reply (if no athlete message, just check-in):".
    let formattedInput = message
      ? `Athlete message: "${message}"`
      : `Context dump for coach-initiated briefing (no athlete message — do not invent one, do not write a reply or check-in section).`;

    if (context.coachingMemory) {
      const m = context.coachingMemory;
      const parts = [];
      const hasRaces = (m.races?.length > 0) || (m.pastRaces?.length > 0);
      if (hasRaces) {
        // Partition into upcoming vs recently completed. A race in the past
        // is NOT a target — dumping past races under "Target races" was
        // causing the model to write taper/sharpen briefings for races the
        // athlete had already finished (July 2026 "Holkham Half sharpen"
        // bug). Scans both `races` (which now holds only upcoming + entries
        // <=30d past thanks to the client sanitizer) and `pastRaces`
        // (belt-and-braces: a stale merge might leave a 20d-past entry in
        // pastRaces, and we still want the recovery framing to fire).
        const todayMidnight = new Date(); todayMidnight.setHours(0, 0, 0, 0);
        const upcoming = [];
        const recentlyCompleted = [];
        const seen = new Set();  // dedup by name|date across the two arrays
        const consider = (r, alreadyArchived) => {
          if (!r || !r.date) { if (r && !alreadyArchived) upcoming.push(r); return; }
          const key = `${(r.name || '').toLowerCase().trim()}|${r.date}`;
          if (seen.has(key)) return;
          seen.add(key);
          // Parse strict ISO as local midnight (matches sanitizeRaces).
          const isoMatch = /^\d{4}-\d{2}-\d{2}$/.test(r.date);
          if (!isoMatch) { if (!alreadyArchived) upcoming.push(r); return; }
          const [y, mo, d] = r.date.split('-').map(Number);
          const raceLocal = new Date(y, mo - 1, d);
          const daysDiff = Math.round((raceLocal - todayMidnight) / 86400000);
          if (daysDiff >= 0 && !alreadyArchived) upcoming.push(r);
          else if (daysDiff < 0 && daysDiff >= -30) recentlyCompleted.push({ ...r, daysAgo: -daysDiff });
          // >30d past — silently dropped from the prompt (archive-only).
        };
        for (const r of (m.races || [])) consider(r, false);
        for (const r of (m.pastRaces || [])) consider(r, true);
        if (upcoming.length > 0) {
          // Show each race with its canonical targetRaceId explicitly so the
          // structured-output path doesn't invent composite IDs like
          // "Holkham_Half_2026-07-05". The grouping logic in TrainingPlanPage
          // matches `targetRaceId` against the race's ISO date string — any
          // other format silently orphans the phase into its own bogus group.
          const raceLines = upcoming.map(r => {
            const name = r.name || r.distance || 'Race';
            const goal = r.goal ? ` (goal: ${r.goal})` : '';
            return `${name} on ${r.date}${goal} — use targetRaceId: "${r.date}"`;
          });
          parts.push(`Target races (use the exact ISO date as targetRaceId in coach-actions proposals):\n    - ${raceLines.join('\n    - ')}`);
        }
        if (recentlyCompleted.length > 0) {
          const doneLines = recentlyCompleted
            .sort((a, b) => a.daysAgo - b.daysAgo)
            .map(r => {
              const name = r.name || r.distance || 'Race';
              const when = r.daysAgo === 0 ? 'today' : r.daysAgo === 1 ? 'yesterday' : `${r.daysAgo} days ago`;
              return `${name} on ${r.date} (${when})`;
            });
          parts.push(`Recently completed races (past 30 days — NOT upcoming targets, do NOT prescribe race prep, taper, or "sharpen" for these):\n    - ${doneLines.join('\n    - ')}`);
        }
      }
      if (m.fitnessLevel) parts.push(`Fitness level: ${m.fitnessLevel}`);
      if (m.goals) parts.push(`Training goals: ${m.goals}`);
      if (m.preferences) parts.push(`Training preferences/constraints: ${m.preferences}`);
      if (m.strengthPattern) parts.push(`Strength & conditioning routine: ${m.strengthPattern}`);
      if (m.injuries?.length > 0) parts.push(`Current/recent injuries: ${m.injuries.map(i => i.description).join(', ')}`);
      if (Array.isArray(m.noteEntries) && m.noteEntries.length > 0) {
        const durable = m.noteEntries.filter(e => e.kind === 'durable').map(e => e.text);
        const transient = m.noteEntries.filter(e => e.kind !== 'durable').map(e => e.text);
        if (durable.length) parts.push(`Durable facts: ${durable.join('; ')}`);
        if (transient.length) parts.push(`Recent / time-sensitive observations (verify with athlete if older than a few weeks): ${transient.join('; ')}`);
      } else if (m.notes) {
        // Legacy fallback before migration on first load.
        parts.push(`Additional notes: ${m.notes}`);
      }
      if (parts.length > 0) {
        formattedInput += `\n\nCoaching memory (key facts remembered from past sessions):`;
        parts.forEach(p => formattedInput += `\n- ${p}`);
      }
    }

    // Detect Strava-tagged race activities in the past 7 days. Strava's
    // workout_type is 1 for a Run race and 11 for a Ride race — these come
    // through as workoutType on the enriched workout objects. A race in this
    // window is a strong signal that the current week is post-race recovery,
    // and the model should stop narrating that ride/run as a "training
    // simulation" or prescribing further intensity for it.
    if (Array.isArray(context.recentWorkouts) && context.recentWorkouts.length > 0) {
      const now = Date.now();
      const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
      const raceWorkouts = context.recentWorkouts.filter(w => {
        if (w.workoutType !== 1 && w.workoutType !== 11) return false;
        if (!w.date) return false;
        const t = new Date(w.date).getTime();
        if (isNaN(t)) return false;
        const ageMs = now - t;
        return ageMs >= 0 && ageMs <= sevenDaysMs;
      });
      if (raceWorkouts.length > 0) {
        const lines = raceWorkouts
          .sort((a, b) => new Date(b.date) - new Date(a.date))
          .map(w => {
            const daysAgo = Math.max(0, Math.round((now - new Date(w.date).getTime()) / 86400000));
            const when = daysAgo === 0 ? 'today' : daysAgo === 1 ? 'yesterday' : `${daysAgo} days ago`;
            const name = w.name || w.title || `${w.type} race`;
            const dist = w.distance ? ` — ${w.distance}km` : '';
            return `${name} (${w.type})${dist}, ${when}`;
          });
        formattedInput += `\n\nRECENT RACE (Strava-tagged, past 7 days — this is a COMPLETED race, not a training session; the current week is post-race recovery):\n- ${lines.join('\n- ')}`;
      }
    }

    if (context.athleteProfile) {
      const p = context.athleteProfile;
      formattedInput += `\n\nAthlete profile:`;
      if (p.background?.sportFocus) {
        const { label } = this._getSportContext(p.background.sportFocus);
        formattedInput += ` Sport focus: ${label}.`;
      }
      if (p.name) formattedInput += ` Name: ${p.name}.`;
      if (p.experience) formattedInput += ` Experience: ${p.experience}.`;
      if (p.goals) formattedInput += ` Goals: ${p.goals}.`;
      if (p.weeklyHours) formattedInput += ` Available weekly hours: ${p.weeklyHours}.`;
      if (p.races) formattedInput += ` Upcoming races: ${p.races}.`;
    }

    if (context.trainingSummary) {
      formattedInput += `\n\n${context.trainingSummary}`;
    }

    if (context.athleteZones) {
      const formatZoneLine = (zones, unit) => {
        return zones.map(z => {
          const maxStr = z.max === -1 ? `${z.min}+` : `${z.min}-${z.max}`;
          return `Z${z.zone} ${maxStr}${unit}`;
        }).join(' · ');
      };
      const parts = [];
      if (context.athleteZones.heartRate?.length) {
        parts.push(`HR: ${formatZoneLine(context.athleteZones.heartRate, 'bpm')}`);
      }
      if (context.athleteZones.power?.length) {
        parts.push(`Power: ${formatZoneLine(context.athleteZones.power, 'W')}`);
      }
      if (parts.length) {
        formattedInput += `\n\nAthlete training zones (Strava-configured — use these when prescribing intensity):\n${parts.join('\n')}`;
      }
    }

    if (context.wellnessData?.length && !isIdFocused) {
      const wellnessLimit = isMinimal ? 3 : 7;
      const recent = context.wellnessData.slice(0, wellnessLimit);
      formattedInput += `\n\nRecent wellness data (intervals.icu, last ${recent.length} days — newest first):`;
      recent.forEach(d => {
        const rawDate = d.date ? String(d.date).slice(0, 10) : null;
        const dateStr = rawDate
          ? new Date(`${rawDate}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
          : 'Unknown';
        const parts = [];
        if (d.hrv != null) parts.push(`HRV ${d.hrv}`);
        if (d.restingHR != null) parts.push(`RHR ${d.restingHR}bpm`);
        if (d.sleepSecs != null) {
          const hrs = (d.sleepSecs / 3600).toFixed(1);
          parts.push(d.sleepScore != null ? `Sleep ${hrs}h (score ${d.sleepScore})` : `Sleep ${hrs}h`);
        }
        if (d.readiness != null) parts.push(`Readiness ${d.readiness}`);
        if (d.feel != null) parts.push(`Feel ${d.feel}/5`);
        if (d.motivation != null) parts.push(`Motivation ${d.motivation}/10`);
        if (d.legscore != null) parts.push(`Legs ${d.legscore}/10`);
        if (parts.length) formattedInput += `\n${dateStr}: ${parts.join(' · ')}`;
      });
    }

    const formatDurationMinutes = (seconds) => {
      if (!seconds) return null;
      return `${Math.round(seconds / 60)}min`;
    };

    const formatWorkoutLine = (workout, index, { noteLimit = 250, includeDeepDetail = false } = {}) => {
      const rawDate = workout.date ? String(workout.date).slice(0, 10) : null;
      const date = rawDate
        ? new Date(`${rawDate}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) + ` (${rawDate})`
        : 'Unknown date';

      let line = `\n${index + 1}. [${date}] ${workout.type?.toUpperCase() || 'WORKOUT'}: ${workout.name || ''}`;
      if (workout.sportType && workout.sportType.toLowerCase() !== workout.type?.toLowerCase()) {
        line += ` | subtype: ${workout.sportType}`;
      }
      if (workout.duration) line += ` | ${formatDurationMinutes(workout.duration)}`;
      if (workout.elapsedTime && workout.duration && workout.elapsedTime > workout.duration + 60) {
        line += ` | elapsed: ${formatDurationMinutes(workout.elapsedTime)}`;
      }
      if (workout.distance) {
        const km = (workout.distance / 1000).toFixed(1);
        line += ` | ${km}km`;
        if (workout.duration && workout.distance > 0) {
          if (workout.type === 'run') {
            const paceSecPerKm = workout.duration / (workout.distance / 1000);
            const paceMin = Math.floor(paceSecPerKm / 60);
            const paceSec = Math.round(paceSecPerKm % 60).toString().padStart(2, '0');
            line += ` | ${paceMin}:${paceSec}/km`;
          } else if (workout.type === 'bike') {
            const speedKph = (workout.averageSpeed || ((workout.distance / 1000) / (workout.duration / 3600))) * 3.6;
            line += ` | ${speedKph.toFixed(1)}km/h`;
          }
        }
      }
      if (workout.averageHeartRate) {
        line += ` | avg HR: ${Math.round(workout.averageHeartRate)}bpm`;
        if (workout.maxHeartRate) line += ` (max: ${Math.round(workout.maxHeartRate)}bpm)`;
      }
      if (workout.averagePower) {
        line += ` | avg power: ${Math.round(workout.averagePower)}W`;
        if (workout.weightedAveragePower) line += ` (weighted: ${Math.round(workout.weightedAveragePower)}W)`;
        if (workout.maxPower) line += ` (max: ${Math.round(workout.maxPower)}W)`;
        if (workout.deviceWatts === false) line += ` estimated`;
      }
      if (workout.elevation) line += ` | elev: ${Math.round(workout.elevation)}m`;
      if (workout.elevHigh != null && workout.elevLow != null) {
        line += ` | elev range: ${Math.round(workout.elevLow)}-${Math.round(workout.elevHigh)}m`;
      }
      if (workout.tss) line += ` | suffer score: ${workout.tss}`;
      if (workout.hrZones) {
        const z = workout.hrZones;
        line += ` | HR zones (min): Z1:${z.z1||0} Z2:${z.z2||0} Z3:${z.z3||0} Z4:${z.z4||0} Z5:${z.z5||0}`;
      }

      const tags = [
        workout.trainer ? 'trainer' : null,
        workout.commute ? 'commute' : null,
        workout.manual ? 'manual' : null,
      ].filter(Boolean);
      if (tags.length) line += ` | flags: ${tags.join(', ')}`;

      if (workout.gearName) line += ` | gear: ${workout.gearName}`;
      if (workout.feeling) line += ` | RPE: ${workout.feeling}/5`;
      if (workout.description) line += ` | Notes: ${workout.description.substring(0, noteLimit)}`;

      if (!includeDeepDetail) return line;

      if (workout.deviceName) line += ` | device: ${workout.deviceName}`;

      if (workout.splitsMetric?.length > 0) {
        const splitPreview = workout.splitsMetric
          .slice(0, 4)
          .map(split => {
            const splitLabel = `split ${split.split}`;
            if (workout.type === 'run' && split.distance > 0 && split.elapsedTime) {
              const paceSecPerKm = split.elapsedTime / (split.distance / 1000);
              const paceMin = Math.floor(paceSecPerKm / 60);
              const paceSec = Math.round(paceSecPerKm % 60).toString().padStart(2, '0');
              return `${splitLabel} ${paceMin}:${paceSec}/km`;
            }
            if (split.averageSpeed) {
              return `${splitLabel} ${(split.averageSpeed * 3.6).toFixed(1)}km/h`;
            }
            return `${splitLabel} ${formatDurationMinutes(split.elapsedTime)}`;
          })
          .join('; ');
        line += ` | splits: ${splitPreview}`;
      }

      if (workout.laps?.length > 0) {
        const lapPreview = workout.laps
          .slice(0, 3)
          .map(lap => `${lap.name || `lap ${lap.id}`}: ${formatDurationMinutes(lap.elapsedTime) || 'n/a'}`)
          .join('; ');
        line += ` | laps: ${workout.laps.length} total (${lapPreview})`;
      }

      if (workout.bestEfforts?.length > 0) {
        const efforts = workout.bestEfforts
          .slice(0, 3)
          .map(effort => `${effort.name || 'effort'} ${formatDurationMinutes(effort.elapsedTime) || ''}`.trim())
          .join('; ');
        line += ` | best efforts: ${efforts}`;
      }

      if (workout.segmentEfforts?.length > 0) {
        const segments = workout.segmentEfforts
          .slice(0, 3)
          .map(effort => `${effort.name || 'segment'} ${formatDurationMinutes(effort.elapsedTime) || ''}`.trim())
          .join('; ');
        line += ` | segment efforts: ${segments}`;
      }

      return line;
    };

    if (context.recentWorkouts && context.recentWorkouts.length > 0) {
      const workoutsToShow = recentCutoffDate
        ? context.recentWorkouts.filter(w => (String(w.date || '').slice(0, 10) || '') >= recentCutoffDate)
        : context.recentWorkouts;
      if (workoutsToShow.length > 0) {
        formattedInput += `\n\nDetailed workout log (${workoutsToShow.length} most recent sessions, newest first):`;
        workoutsToShow.forEach((workout, index) => {
          formattedInput += formatWorkoutLine(workout, index, { noteLimit: 250 });
        });
      }
    }

    // Attached activities — user explicitly selected these for deep analysis
    if (context.attachedActivities && context.attachedActivities.length > 0) {
      formattedInput += `\n\n=== ATTACHED ACTIVITIES FOR DEEP ANALYSIS (the user specifically selected these — provide detailed analysis) ===`;
      context.attachedActivities.forEach((workout, index) => {
        formattedInput += formatWorkoutLine(workout, index, { noteLimit: 500, includeDeepDetail: true });
      });
    }

    if (context.plannedWorkouts && context.plannedWorkouts.length > 0) {
      formattedInput += `\n\nUpcoming planned workouts (from training platform):`;
      context.plannedWorkouts.slice(0, 7).forEach((workout, index) => {
        formattedInput += `\n${index + 1}. ${workout.date}: ${workout.name || workout.category}`;
        if (workout.planned_duration) formattedInput += ` - ${Math.round(workout.planned_duration / 60)}min`;
        if (workout.description) formattedInput += ` - ${workout.description}`;
      });
    }

    if (!isMinimal && context.calendarWorkouts && context.calendarWorkouts.length > 0) {
      formattedInput += `\n\nAI-generated calendar workouts (${context.calendarWorkouts.length} total — to remove any, propose a \`remove_workouts\` action with the exact IDs shown below):`;
      context.calendarWorkouts.forEach((w, i) => {
        const rawDate = w.date ? String(w.date).slice(0, 10) : null;
        const date = rawDate
          ? new Date(`${rawDate}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) + ` (${rawDate})`
          : 'Unknown';
        formattedInput += `\n${i + 1}. ID:${w.id} | [${date}] ${(w.sport || '').toUpperCase()}: ${w.title || ''}`;
        if (w.duration) formattedInput += ` | ${w.duration}min`;
        if (w.zone) formattedInput += ` | ${w.zone}`;
        if (w.description) formattedInput += ` | ${w.description.substring(0, 60)}`;
        formattedInput += ` | ${w.status || 'planned'}`;
      });
    }

    if (!isMinimal && !isIdFocused && context.completedPlannedWorkouts && context.completedPlannedWorkouts.length > 0) {
      formattedInput += `\n\nRecently completed planned sessions (with athlete feedback):`;
      context.completedPlannedWorkouts.forEach((workout, index) => {
        const rawDate2 = workout.date ? String(workout.date).slice(0, 10) : null;
        const date = rawDate2
          ? new Date(`${rawDate2}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) + ` (${rawDate2})`
          : 'Unknown date';
        formattedInput += `\n${index + 1}. [${date}] ${workout.sport?.toUpperCase() || 'WORKOUT'}: ${workout.title || ''}`;
        if (workout.duration) formattedInput += ` | ${Math.round(workout.duration)}min`;
        if (workout.rpe) formattedInput += ` | RPE: ${workout.rpe}/5`;
        if (workout.completionNote) formattedInput += ` | Note: ${workout.completionNote}`;
      });
    }

    if (!isMinimal && context.trainingPlan && (context.trainingPlan.phases?.length > 0 || context.trainingPlan.milestones?.length > 0)) {
      const tp = context.trainingPlan;
      formattedInput += `\n\nCurrent season training plan (${tp.phases.length} phases, ${tp.milestones.length} milestones — use the exact IDs below when proposing \`modify_phase\`, \`remove_phase\`, or \`remove_milestone\` actions):`;
      tp.phases.forEach((p, i) => {
        formattedInput += `\n${i + 1}. Phase ID:${p.id} | ${p.name} (${p.type}) | ${p.startDate} – ${p.endDate}`;
        if (p.weeklyTSS) formattedInput += ` | ~${p.weeklyTSS} TSS/wk`;
        if (p.targetRaceId) formattedInput += ` | race:${p.targetRaceId}`;
      });
      if (tp.milestones.length > 0) {
        formattedInput += `\nMilestones:`;
        tp.milestones.forEach((m, i) => {
          formattedInput += `\n${i + 1}. ID:${m.id} | ${m.label} (${m.type}) | ${m.date}`;
          if (m.targetRaceId) formattedInput += ` | race:${m.targetRaceId}`;
        });
      }
    }

    return formattedInput;
  }

  // Generate a natural-language athlete profile summary from profile fields + coaching memory.
  // Only upcoming races belong in a bio — completed/archived races are noise here.
  async generateProfileSummary(profile, memory) {
    const upcomingRaces = Array.isArray(memory?.races)
      ? memory.races.filter(r => {
          const status = r?.status || 'upcoming';
          if (status !== 'upcoming') return false;
          if (!r?.date) return true;
          if (!/^\d{4}-\d{2}-\d{2}$/.test(r.date)) return true;  // fuzzy → keep
          const [y, m, d] = r.date.split('-').map(Number);
          const today = new Date(); today.setHours(0, 0, 0, 0);
          return new Date(y, m - 1, d).getTime() >= today.getTime();
        })
      : [];
    const lines = [
      profile.name        && `Name: ${profile.name}`,
      profile.experience  && `Experience: ${profile.experience}`,
      profile.weeklyHours && `Weekly training hours available: ${profile.weeklyHours}`,
      profile.goals       && `Goals: ${profile.goals}`,
      profile.races       && `Upcoming races (self-reported): ${profile.races}`,
      upcomingRaces.length > 0 && `Race details from coaching: ${upcomingRaces.map(r =>
        `${r.name || r.distance || 'Race'} on ${r.date}${r.goal ? ' (goal: ' + r.goal + ')' : ''}`
      ).join(', ')}`,
      memory?.fitnessLevel  && `Fitness level: ${memory.fitnessLevel}`,
      memory?.injuries?.length > 0 && `Current injuries: ${memory.injuries.map(i => i.description).join(', ')}`,
      memory?.goals         && `Training goals from coaching: ${memory.goals}`,
      memory?.preferences   && `Preferences/constraints: ${memory.preferences}`,
      Array.isArray(memory?.noteEntries) && memory.noteEntries.length > 0 &&
        `Coaching notes: ${memory.noteEntries.filter(e => e.kind === 'durable').map(e => e.text).join('; ') || memory.noteEntries.map(e => e.text).join('; ')}`,
    ].filter(Boolean).join('\n');

    const messages = [
      {
        role: 'system',
        content: `You write concise athlete bios for a triathlon coaching app. Write 2–3 sentences in a warm, coach-like tone. Be specific — mention their experience level, race targets, goals, and any notable context. Use third person if a name is given, otherwise say "This athlete". Do not be generic.`
      },
      {
        role: 'user',
        content: `Write an athlete profile summary from this information:\n${lines}`
      }
    ];

    return await this._callCompletion(messages, 150, 0.7);
  }

  // Extract new coaching facts from a single exchange — runs in background, cheap call.
  // `notes` is returned as an array of {text, kind} where kind is 'durable' or 'transient'.
  async extractMemoryUpdate(userMessage, aiResponse, existingMemory) {
    try {
      const knownNotes = (existingMemory?.noteEntries || []).map(e => e.text).slice(0, 20);
      // Include BOTH races and pastRaces in "already known" so the model
      // doesn't re-extract a race the athlete just mentioned as historical
      // context (e.g. "back when I did Slateman…"). Also strips fields the
      // extractor shouldn't influence (status, daysAgo).
      const allKnownRaces = [
        ...(Array.isArray(existingMemory?.races) ? existingMemory.races : []),
        ...(Array.isArray(existingMemory?.pastRaces) ? existingMemory.pastRaces : []),
      ].map(r => ({ name: r.name, date: r.date, distance: r.distance, goal: r.goal }));
      const known = existingMemory
        ? `Already known: races=${JSON.stringify(allKnownRaces)}, fitnessLevel="${existingMemory.fitnessLevel}", goals="${existingMemory.goals}", strengthPattern="${existingMemory.strengthPattern || ''}", injuries=${JSON.stringify(existingMemory.injuries)}, notes=${JSON.stringify(knownNotes)}.`
        : 'Nothing known yet.';

      const messages = [
        {
          role: 'system',
          content: `You extract structured coaching facts from a triathlon coaching exchange. ${known}

Return ONLY a JSON object containing NEW facts not already known (omit fields with no new data, return {} if nothing new):
{"races":[{"name":"...","date":"YYYY-MM-DD (preferred) or Month YYYY if the exact day is unknown","distance":"sprint/olympic/half/ironman","goal":"..."}],"injuries":[{"description":"..."}],"fitnessLevel":"...","goals":"...","preferences":"...","strengthPattern":"...","notes":[{"text":"...","kind":"durable"|"transient"}]}

Rules for "races":
- Only extract FUTURE target races the athlete is training for. Do NOT extract a race the athlete mentions as a historical reference or a result they're recalling.
- Prefer an exact ISO date (YYYY-MM-DD) when the athlete gives the day. Only fall back to "Month YYYY" when the day is genuinely unknown.
- Do NOT re-extract a race that appears in the known list above, even if the athlete rephrased its name or date.

Rules for "notes":
- Each note must be ONE concise fact (max ~120 chars). Do NOT write paragraphs.
- kind="durable": stable preferences, baseline facts, equipment, life context, race goals/strategy. Examples: "Prefers morning long rides", "Has access to indoor trainer", "Targets sub-6h half ironman".
- kind="transient": ONGOING current state that is expected to change soon — recent fatigue/soreness, this-week training phase, mood, recent overreaching. Examples: "Currently fatigued (TSB -25)", "QL soreness this week", "In a recovery block".
- Do NOT record one-off dated events or completed-workout logs (e.g. "Did strength on 2026-06-02", "Ran 10k yesterday", "Swam Tuesday"). Those already live in the activity data. Only record a fact if it is a stable preference/baseline (durable) or an ongoing state (transient) — never a specific past-dated occurrence.
- transient notes must describe a STATE, not an event, and must NOT contain a specific past calendar date.
- Skip generic coaching advice the AI gave — only record facts ABOUT the athlete.
- Skip facts already in the known list above.
- Return empty array (or omit) if no new athlete facts emerged.

Rules for "strengthPattern":
- Single short string capturing the athlete's current strength & conditioning routine (frequency, location/equipment, focus). Max ~120 chars.
- Examples: "2x/week, home bodyweight, Essential 6 circuit", "1x/week gym, deadlift + push/pull", "None — wants to start but no equipment", "Currently injured, no lifting".
- Only update when the athlete explicitly says something new about their strength routine. Do not infer.`
        },
        {
          role: 'user',
          content: `Athlete said: "${userMessage}"\n\nCoach replied: "${aiResponse.substring(0, 400)}"`
        }
      ];

      // Enforce JSON-object output. Without this, gpt-5-family models frequently
      // wrap the result in prose or an explanation, JSON.parse throws, and the
      // catch below silently returns null — so facts were never getting saved.
      const text = await this._callCompletion(messages, 350, 0, { type: 'json_object' });
      // Strip any markdown code fences if model adds them
      const jsonText = text.replace(/^```json?\n?/, '').replace(/\n?```$/, '');
      const parsed = JSON.parse(jsonText);

      // Back-compat: if model returns notes as a string, wrap it as a transient entry.
      if (typeof parsed.notes === 'string') {
        const trimmed = parsed.notes.trim();
        parsed.notes = trimmed ? [{ text: trimmed, kind: 'transient' }] : [];
      }
      if (Array.isArray(parsed.notes)) {
        parsed.notes = parsed.notes
          .filter(n => n && typeof n.text === 'string' && n.text.trim())
          .map(n => ({
            text: n.text.trim(),
            kind: n.kind === 'durable' ? 'durable' : 'transient',
          }));
        if (parsed.notes.length === 0) delete parsed.notes;
      }

      return Object.keys(parsed).length > 0 ? parsed : null;
    } catch (error) {
      // Silent fail — memory extraction is non-critical
      return null;
    }
  }

  // Compact a list of note entries into a deduped, summarised list (max 10).
  // Preserves the {text, kind} shape; the caller re-stamps ids/timestamps.
  async compactMemoryNotes(entries) {
    if (!Array.isArray(entries) || entries.length === 0) return entries || [];
    try {
      const payload = entries.map(e => ({ text: e.text, kind: e.kind || 'transient' }));
      const messages = [
        {
          role: 'system',
          content: `You compact a triathlon coach's memory notes about a single athlete.

INPUT: a JSON array of {text, kind} where kind is "durable" (stable facts) or "transient" (recent observations).

TASK: return a JSON array of AT MOST 10 entries that:
- Merge duplicates and near-duplicates into one concise entry.
- Drop redundant or superseded items (e.g. multiple notes about the same fatigue episode → keep one).
- Preserve every distinct durable fact (race goals, preferences, equipment, baselines, persistent injuries).
- For transient items, keep at most 3–4 most recent / most relevant; drop stale or repetitive ones.
- Each entry's text must be ONE short fact, max ~120 chars. No paragraphs, no coach-style commentary.
- Preserve "kind" — do not flip durable↔transient.

Return ONLY the JSON array. No prose, no code fences.`
        },
        {
          role: 'user',
          content: JSON.stringify(payload)
        }
      ];
      const text = await this._callCompletion(messages, 800, 0);
      const jsonText = text.replace(/^```json?\n?/, '').replace(/\n?```$/, '').trim();
      const parsed = JSON.parse(jsonText);
      if (!Array.isArray(parsed)) return entries;
      const cleaned = parsed
        .filter(n => n && typeof n.text === 'string' && n.text.trim())
        .map(n => ({
          text: n.text.trim(),
          kind: n.kind === 'durable' ? 'durable' : 'transient',
        }));
      return cleaned.length > 0 ? cleaned : entries;
    } catch (error) {
      // On failure, keep original entries — never lose data.
      return entries;
    }
  }

  async generateSessionOpener(athleteContext) {
    try {
      const now = new Date();
      const todayISO = now.toISOString().slice(0, 10);
      const todayReadable = now.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
      const dayOfWeek = now.getDay(); // 0=Sun, 1=Mon, ..., 5=Fri, 6=Sat

      const sportFocus = athleteContext?.athleteProfile?.background?.sportFocus || 'triathlon';
      const { label: sportLabel, override: sportOverride } = this._getSportContext(sportFocus);

      let dayGuidance;
      if (dayOfWeek === 1) {
        dayGuidance = `Today is Monday — open with a brief week-ahead preview: given their recent load and training balance, what is the priority focus and key goal for this week?`;
      } else if (dayOfWeek === 5 || dayOfWeek === 6) {
        dayGuidance = `Today is ${dayOfWeek === 5 ? 'Friday' : 'Saturday'} — briefly recap how the training week has gone (volume, any standout sessions), then suggest a smart weekend approach (hard or easy depending on form/fatigue).`;
      } else {
        dayGuidance = `Lead with their most recent completed workout (within the last 2 days if possible) — name the activity and one specific thing that stood out (pace, power, HR, feel). If nothing in the last 2 days, mention the next planned session.`;
      }

      // Build a supplemental context note for special conditions
      const extras = [];
      if (athleteContext.newWorkoutSinceLastSession) {
        extras.push(`NOTE: The athlete has a new workout logged since their last coaching session — prioritise giving specific feedback on it.`);
      }

      let contextString = this.formatUserInput('', athleteContext, null);
      if (extras.length) contextString = extras.join('\n') + '\n\n' + contextString;

      const messages = [
        {
          role: 'system',
          content: `TODAY'S DATE: ${todayReadable} (${todayISO}).\n\nYou are an expert ${sportLabel} coach opening a new coaching session. Write a focused 2–3 sentence briefing that is immediately useful to the athlete.\n\n${dayGuidance}\n\nAdditionally, weave in any of the following that apply:\n- If the context contains a RECENT RACE line or a "Recently completed races" entry within the past 7 days, this is a POST-RACE RECOVERY week. Acknowledge the race by name, ask briefly about soreness/how it went, and keep the recommendation to easy Z1–Z2 movement, mobility, or full rest. Do NOT prescribe race prep, taper, or "sharpen" work for a race that has already happened, and do NOT narrate the race itself as a training simulation.\n- If ACWR (ATL÷CTL) is above 1.3 or fatigue (ATL) is notably higher than fitness (CTL), flag recovery urgency briefly\n- If a race is within 14 days AND it appears under "Target races" (not "Recently completed"), lead with race-specific prep (taper, logistics, nutrition timing)\n- If zero strength sessions (type "strength") appear in the last 7 days AND the athlete is not in race week, taper, or post-race recovery, end with a short, specific strength prompt (e.g. "Squeeze in a 25-min Essential 6 circuit this week — your legs will thank you on race day"). Do this in addition to the day-of-week guidance, not instead of it.\n\nRules:\n- Stay under 80 words\n- End with ONE concrete, actionable recommendation (not a question)\n- Do not start with "I can see", "I notice", "Great", or "Well"\n- Be specific — name the activity, the distance, or the metric that matters\n- Do not ask questions${sportOverride}`
        },
        {
          role: 'user',
          content: contextString
        }
      ];
      const result = await this._callCompletion(messages, 150, 0.7);
      return trimProseToLastSentence(result);
    } catch (error) {
      return null;
    }
  }

  async generateDailyBriefing(athleteContext) {
    try {
      const contextString = this.formatUserInput('', athleteContext, null);
      const now = new Date();
      const todayReadable = now.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
      const sportFocus = athleteContext?.athleteProfile?.background?.sportFocus || 'triathlon';
      const { label: sportLabel, override: sportOverride } = this._getSportContext(sportFocus);

      // Use pre-computed flags (calculated from full workout history in App.js) to
      // build a deterministic bullet-3 priority rule rather than hoping the model infers it.
      const { hasStrengthGap, isRaceWeekOrTaper, daysToNextRace, nextRaceName, recentRaceCompleted } = athleteContext.flags || {};
      let bullet3Rule;
      if (recentRaceCompleted) {
        const { name: rrName, daysAgo: rrDays } = recentRaceCompleted;
        const when = rrDays === 0 ? 'today' : rrDays === 1 ? 'yesterday' : `${rrDays} days ago`;
        bullet3Rule = `BULLET 3 MUST cover post-race recovery: the athlete completed "${rrName}" ${when}. Prescribe easy Z1–Z2 movement, mobility, sleep, or full rest — NOT intensity, taper, or race prep. Do NOT treat this race as an upcoming target.`;
      } else if (isRaceWeekOrTaper && daysToNextRace != null) {
        bullet3Rule = `BULLET 3 MUST cover race/taper management: "${nextRaceName}" is ${daysToNextRace} day${daysToNextRace === 1 ? '' : 's'} away — taper execution, final logistics, or race-day tactic.`;
      } else if (hasStrengthGap) {
        bullet3Rule = `BULLET 3 MUST cover strength gap: zero strength sessions logged in the last 7 days. Name a specific Essential 6 exercise or short circuit (e.g. "15-min Essential 6 circuit: split squat + single-leg RDL + side plank").`;
      } else {
        bullet3Rule = `Bullet 3: use the highest-priority watch-out available — ACWR if outside 0.8–1.3, a discipline gap (e.g. no swim in 7+ days), or a nutrition/recovery signal.`;
      }

      const messages = [
        {
          role: 'system',
          content: `TODAY'S DATE: ${todayReadable}.\n\nYou are an expert ${sportLabel} coach. Write a daily briefing card — exactly 3 bullet points using the • symbol, each ≤18 words. Cover: (1) form/load status today, (2) single highest-priority session or focus for this week, (3) ${bullet3Rule}\n\nHARD RULES:\n- Do not start with a title, heading, or label of any kind\n- Total response must be under 90 words\n- No headers, no sub-sections, no numbered lists\n- No JSON, no coach-action blocks of any kind\n- No filler phrases ("Great!", "Well done", "I can see")\n- Be specific — name the workout, metric, or discipline${sportOverride}`
        },
        {
          role: 'user',
          content: contextString
        }
      ];
      const result = await this._callCompletion(messages, 250, 0.5);
      return sanitizeBulletResponse(result);
    } catch (error) {
      return null;
    }
  }

  async generateRacePrepBriefing(raceName, daysUntil, athleteContext) {
    try {
      const contextString = this.formatUserInput('', athleteContext, null);
      const sportFocus = athleteContext?.athleteProfile?.background?.sportFocus || 'triathlon';
      const { label: sportLabel, override: sportOverride } = this._getSportContext(sportFocus);
      const messages = [
        {
          role: 'system',
          content: `You are an expert ${sportLabel} coach. Your athlete has a race called "${raceName}" in ${daysUntil} day${daysUntil === 1 ? '' : 's'}. Write a focused race-week briefing with exactly 3 short bullet points covering the most important things they should action RIGHT NOW, relevant to ${sportLabel}. Cover: taper/load management, race-day logistics (appropriate gear/kit/nutrition for ${sportLabel}), and one mental/tactical tip. Keep each bullet under 25 words. No fluff. Do not use sub-headings, just bullets starting with •.${sportOverride}`
        },
        {
          role: 'user',
          content: contextString
        }
      ];
      const result = await this._callCompletion(messages, 160, 0.6);
      return sanitizeBulletResponse(result);
    } catch (error) {
      return null;
    }
  }

  // On-demand "coach's take" for a single activity, used by the Activity Detail
  // insight card (Issue #9). Deliberately lean: this is NOT built from the full
  // formatUserInput() context dump (too expensive/slow for a single-activity,
  // on-tap call) — just the deterministic analysis already computed client-side
  // (buildActivityInsight from utils/activityInsights.js) plus a few key facts.
  // The deterministic analysis is the source of truth and works standalone;
  // this method only adds optional narrative color, so any failure here must
  // never block the card from rendering its deterministic content.
  async generateActivityInsight(workout, insight, athleteContext = {}) {
    try {
      const sportFocus = athleteContext?.athleteProfile?.background?.sportFocus || 'triathlon';
      const { label: sportLabel, override: sportOverride } = this._getSportContext(sportFocus);

      const facts = [
        `Activity: "${workout?.name || workout?.type || 'Workout'}" (${workout?.type || 'unknown type'})`,
        workout?.duration ? `Duration: ${Math.round(workout.duration / 60)} min` : null,
        workout?.distance ? `Distance: ${(workout.distance / 1000).toFixed(1)} km` : null,
        workout?.averageHeartRate ? `Avg HR: ${Math.round(workout.averageHeartRate)} bpm` : null,
        workout?.averagePower ? `Avg power: ${Math.round(workout.averagePower)} W` : null,
        `Deterministic classification: ${insight?.effort?.category || 'unclassified'} (${insight?.effort?.reasons?.[0] || 'no basis available'})`,
        insight?.hrZoneTargeting?.available ? `HR zone targeting: ${insight.hrZoneTargeting.message}` : null,
        insight?.goal?.message ? `Goal relevance: ${insight.goal.message}` : null,
        insight?.fatigueNote ? `Fatigue/load context: ${insight.fatigueNote}` : null,
      ].filter(Boolean).join('\n');

      const messages = [
        {
          role: 'system',
          content: `You are an expert ${sportLabel} coach. The athlete tapped into the details of ONE past activity. A deterministic system already classified the effort and computed the facts below — do NOT repeat them verbatim or restate the numbers. Instead, write 1–2 short sentences (max 40 words) of genuinely useful coaching color: what this session likely means for their training, or one thing to notice next time. Do not open with "Great", "Nice", "I see", or a restatement of the activity name. No bullets, no headings, no questions.${sportOverride}`
        },
        { role: 'user', content: facts }
      ];
      const result = await this._callCompletion(messages, 100, 0.6);
      return trimProseToLastSentence(result);
    } catch (error) {
      return null;
    }
  }

  async testConnection() {
    try {
      const content = await this._callCompletion(
        [{ role: 'user', content: 'Hello' }], 5, 0
      );
      return { success: true, response: content };
    } catch (error) {
      console.error('AI connection test failed:', error);
      if (error.status === 401) {
        return { success: false, error: 'Invalid API key or access token.' };
      } else if (error.status === 403) {
        return { success: false, error: 'Access denied. Your account is not authorised for this app.' };
      } else if (error.status === 429) {
        return { success: false, error: 'Rate limit exceeded. Your key is valid but over quota.' };
      } else if (error.status === 402) {
        return { success: false, error: 'No API credits. Please add billing to your OpenAI account.' };
      } else {
        return { success: false, error: `Connection failed: ${error.message}` };
      }
    }
  }

  async getAccountInfo() {
    try {
      const startTime = Date.now();
      await this._callCompletion([{ role: 'user', content: 'Test' }], 1, 0);
      return {
        success: true,
        connected: true,
        responseTime: Date.now() - startTime,
        model: this.deployment,
        provider: this.mode === 'proxy' ? 'proxy' : this.provider,
        status: 'Active'
      };
    } catch (error) {
      return { success: false, connected: false, error: { message: error.message } };
    }
  }

  async generateReportAnalysis(athleteContext = {}) {
    const now = new Date();
    const todayISO = now.toISOString().slice(0, 10);
    const todayReadable = now.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    const dateContext = `TODAY'S DATE: ${todayReadable} (${todayISO}).`;

    const reportPrompt = `Write a detailed coaching report for this athlete. Structure your response with these exact markdown headings:

## Progress Review
How training has been tracking against goals, volume trends, fitness/fatigue balance. Reference specific metrics (CTL, ATL, TSB) and recent workout data.

## Key Strengths
What's going well — consistency, specific disciplines, improvements observed in the data.

## Areas for Improvement
Gaps, weaknesses, undertrained areas, or imbalances across disciplines.

## Strength & Conditioning
Count strength sessions (type "strength") in the recent workout list. State the count for the last 4 weeks. Assess against phase target (Base: 2-3/wk; Build: 1-2/wk; Peak: 1/wk; Race week: none). If below target, recommend a specific weekly cadence and name 2-3 Essential 6 exercises most relevant to this athlete's disciplines and any injuries flagged. If on target, acknowledge briefly and suggest the next progression (e.g. tempo, eccentric pause, or unilateral focus). This section is mandatory — do not skip it.

## Priority Focus
Top 2-3 things to concentrate on in the next 2-4 weeks, with specific actionable suggestions. Strength-related items count toward this list when relevant.

## Injury & Health Considerations
Only include this section if there are injury or health concerns to flag. If none, skip entirely.

## Recommendations
Specific, actionable advice for the next training block. Reference the athlete's goals, upcoming races, and current fitness state.

## Suggested Key Sessions
Prescribe 2-3 specific workouts for the coming week. Include session type, duration, and intensity targets. Use pace/power/HR zones where the athlete's metrics are known; otherwise use RPE and duration. Be specific (e.g. "5x1km @ threshold with 90s jog recovery"). If the Strength & Conditioning section flagged a gap, include at least one strength session in this list with named Essential 6 exercises, sets/reps, and an estimated duration.

Be specific, reference actual workout data, metrics, and dates. Keep the total to ~600 words. Write in a professional coaching tone — clear, direct, and constructive.

CRITICAL FORMATTING RULES — failure to follow these breaks the report rendering:
1. Do NOT include any preamble, title, summary, or "TRAINING REPORT" header before the first "## " heading. Start your response directly with "## Progress Review".
2. Do NOT include a closing summary at the end. The last section is "## Suggested Key Sessions" — stop there.
3. Use numbered lists (1. 2. 3.) for items within each section.
4. Each numbered item MUST be on its own line, separated from the previous item by a blank line. Example:

1. First item ends here.

2. Second item starts on a new line.

5. Never run "2." straight after the end of item 1's sentence. ALWAYS put a newline before each numbered item.
6. Always include a space after every full stop, comma, and colon. Never write "2026Current" — write "2026. Current".
7. Avoid using markdown emphasis (** or *) inside numbered list items unless absolutely necessary; plain text reads better in PDFs.
8. Do not use horizontal rules (---) or markdown tables.
9. All sessions, timelines, and date references in the Recommendations and Suggested Key Sessions sections MUST be for dates on or after TODAY (${todayISO}). Never prescribe a session for a date that has already passed. Use relative terms (e.g. "this Thursday", "next weekend") rather than calendar dates where possible.`;

    const sportFocus = athleteContext?.athleteProfile?.background?.sportFocus || 'triathlon';
    const sportLabel = {
      triathlon: 'triathlon', duathlon: 'duathlon (run + bike + run)',
      aquabike: 'aquabike (swim + bike)', run: 'running', bike: 'cycling', swim: 'swimming'
    }[sportFocus] || 'triathlon';

    const sportOverride = sportFocus !== 'triathlon'
      ? `\n\nIMPORTANT: This athlete's focus is ${sportLabel}. Tailor ALL analysis to ${sportLabel} specifically.`
      : '';

    const messages = [
      { role: 'system', content: `${dateContext}\n\nYou are an expert ${sportLabel} coach writing a formal training report. Be analytical and data-driven. Do not use conversational tone — this is a professional document, not a chat message.${sportOverride}` },
      { role: 'user', content: this.formatUserInput(reportPrompt, athleteContext, null) }
    ];

    return await this._callCompletion(messages, 2000, 0.5);
  }
}
